import type { ManualDoc, ManualIndex } from "../shell/help/types";

/** API クライアント。エラー形式は {"error":{"code","message"}}(docs/api-conventions.md)。 */
import type { MailDetail, MailDownloadRecord, MailDownloadSummary, MailInboundDetail, MailInboundPage, MailInboundSearch, MailInboundStats, MailMessage, MailRevokeManyResult, MailRule, MailRuleInput, MailSettings, MailStatus } from "../apps/dlp/mailTypes";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly reason?: string,
    readonly retryAfter?: number,
    /** `error` オブジェクト全体(reasons・count など機械可読の手がかり) */
    readonly extra?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export type Page<T> = { items: T[]; total: number };

/** ライセンスの状態。JWT 本文は**返ってこない**(画面にも出さない)。 */
export type LicenseState = {
  state: "ok" | "grace" | "expired" | "missing" | "invalid";
  state_label: string;
  customer: string;
  plan: string;
  expires_at: string | null;
  grace_until: string | null;
  read_only: boolean;
  problem: string;
};

/** 機能表の1行(ライセンス画面の一覧)。 */
export type FeatureRow = {
  key: string;
  app: string;
  label: string;
  depends: string[];
  enabled: boolean;
};

export type LicenseInfo = { license: LicenseState; features: FeatureRow[] };

export type Me = {
  sub: string;
  username: string;
  display_name: string;
  email: string;
  roles: string[];
  apps: string[];
  is_suite_admin: boolean;
  /** 今使える機能キー(`server/app/core/features.py` の表の部分集合) */
  features: string[];
  license: LicenseState;
};

export type AppTile = {
  key: string;
  name: string;
  description: string;
  sort: number;
  enabled: boolean;
  path: string;
  status: "available" | "coming_soon";
  roles: string[];
};

export type AuditRow = {
  id: number;
  actor: string;
  action: string;
  target: string;
  detail: string;
  app: string;
  created_at: string;
};

export type SettingRow = {
  key: string;
  value: Record<string, unknown>;
  updated_at: string | null;
};

// ---- 人事(R2)----------------------------------------------------------------

/** 一覧・詳細で返す従業員(server/app/hr/schemas.py の EmployeeOut と同じ形)。 */
export type Employee = {
  id: number;
  emp_code: string;
  /** 表示名。**サーバーが姓・名から導出する**(入力口は無い) */
  name: string;
  family_name: string;
  given_name: string;
  family_name_kana: string;
  given_name_kana: string;
  /** 姓だけ / 名が空のまま取り込まれた行。一覧に要確認バッジを出す */
  name_needs_review: boolean;
  email: string;
  department: string;
  status: string;
  employment_type: string;
  /** 電話(任意。電話帳同期が配る) */
  work_mobile: string;
  work_phone: string;
  extension: string;
  direct: boolean;
  org_unit_id: number | null;
  org_unit_path: string;
  position_id: number | null;
  position_name: string;
  manager_id: number | null;
  manager_name: string;
  note: string;
  joined_at: string | null;
  left_at: string | null;
  anonymized_at: string | null;
  created_at: string;
  updated_at: string;
  concurrent: Concurrent[];
  /** 貸与中の品目名(貸与品台帳の未返却行)。一覧の「貸与品」列に出す */
  loans: string[];
  /** カスタム項目の値({key: 文字列}。定義は GET /api/hr/custom-fields) */
  extra: Record<string, string>;
};

export type Concurrent = {
  id: number;
  unit_id: number;
  unit_name: string;
  unit_path: string;
  label: string;
  active: boolean;
};

/** 詳細。上長は手動指定が無ければ役職から自動導出した結果が入る。 */
export type EmployeeDetail = Employee & {
  manager_id_resolved: number | null;
  manager_source: string | null; // manual / auto / null
  manager_warning: string;
  position_rank: number | null;
  /** 入社前で履歴も他アプリの事情も無い = 物理削除できる(役割は見ない。画面が hr:admin と組み合わせる) */
  deletable: boolean;
};

/** 登録・更新で送る値。id 項目の 0 は「解除」(旧 PoC と同じ)。 */
export type EmployeeInput = {
  emp_code?: string;
  family_name?: string;
  given_name?: string;
  family_name_kana?: string;
  given_name_kana?: string;
  email?: string;
  employment_type?: string;
  work_mobile?: string;
  work_phone?: string;
  extension?: string;
  status?: string;
  note?: string;
  joined_at?: string | null;
  left_at?: string | null;
  org_unit_id?: number;
  position_id?: number;
  manager_id?: number;
  /** カスタム項目。PATCH では渡した key だけ書き換える(空文字で未設定に戻す) */
  extra?: Record<string, string>;
  /** PATCH のみ。true なら更新後の契約形態で社員番号を採番し直す(移籍。emp_code とは同時に送れない) */
  renumber_emp_code?: boolean;
};

/** 契約形態を変えたとき社員番号を採番し直すべきか(BP059 + 正社員 → AM029) */
export type RenumberPreview = { current: string; next: string; changes: boolean };

/** 従業員のカスタム項目(会社ごとに違う欄)の定義。値は Employee.extra[key]。 */
export type CustomField = {
  id: number;
  key: string;
  label: string;
  type: "text" | "select" | "date";
  options: string[];
  required: boolean;
  show_in_list: boolean;
  searchable: boolean;
  sort: number;
  active: boolean;
  created_at: string;
  updated_at: string;
};

export type CustomFieldInput = {
  key?: string;
  label?: string;
  type?: CustomField["type"];
  options?: string[];
  required?: boolean;
  show_in_list?: boolean;
  searchable?: boolean;
  sort?: number;
  active?: boolean;
};

export type OrgUnit = {
  id: number;
  name: string;
  code: string;
  kind: string;
  kind_label: string;
  parent_id: number | null;
  path: string;
  head_employee_id: number | null;
  head_name: string;
  head_title: string;
  head_title_label: string;
  active: boolean;
  sort: number;
  note: string;
  /** GWS の組織部門(OU)の経路。人が決める(空なら連携の対象外) */
  gws_ou_path: string;
  /** 同期で書き戻る GWS 側の id。画面からは触らない */
  gws_ou_id: string;
  depth: number;
  member_count: number;
  child_count: number;
};

export type OrgUnitInput = {
  name?: string;
  kind?: string;
  parent_id?: number | null;
  code?: string;
  note?: string;
  sort?: number;
  head_employee_id?: number | null;
  head_title?: string;
  active?: boolean;
  gws_ou_path?: string;
};

export type Position = {
  id: number;
  name: string;
  rank: number;
  active: boolean;
  created_at: string;
  updated_at: string;
};

/** 組織図の1箱。d3-org-chart に渡すため親子は id / parent_id で持つ。 */
export type ChartNode = {
  id: number;
  parent_id: number | null;
  name: string;
  kind: string;
  kind_label: string;
  path: string;
  fill: string;
  head: { id: number; name: string; title: string; partner: boolean; note: string } | null;
  member_count: number;
  total_count: number;
  groups: {
    label: string;
    members: {
      id: number;
      name: string;
      status: string;
      partner: boolean;
      note: string;
      position_name: string;
    }[];
  }[];
};

export type ChartOut = { scope: string; layer: string; items: ChartNode[]; total: number };

/** 指揮系統の木。source は manual(手動指定)/ auto(役職からの導出)。 */
export type ReportingNode = {
  employee: {
    id: number;
    emp_code: string;
    name: string;
    email: string;
    status: string;
    org_unit_id: number | null;
    org_unit_path: string;
    position_id: number | null;
    position_name: string;
    position_rank: number | null;
    employment_type: string;
    partner: boolean;
    note: string;
  };
  manager_id: number | null;
  manager_name: string;
  source: string | null;
  warning: string;
  children: ReportingNode[];
};

export type ReportingOut = { scope: string; items: ReportingNode[]; total: number };

// ---- 貸与品台帳・備品マスタ(R2。備品は SaaS カタログには混ぜない)-------------

/** 貸与品台帳の1行。`open` が true なら貸与中(未返却)。 */
export type Loan = {
  id: number;
  employee_id: number;
  employee_name: string;
  emp_code: string;
  employee_email: string;
  item: string;
  section: string;
  request_id: number | null;
  task_id: number | null;
  equipment_id: number | null;
  asset_no: string;
  equipment_name: string;
  lent_at: string | null;
  returned_at: string | null;
  return_request_id: number | null;
  return_task_id: number | null;
  note: string;
  open: boolean;
};

/** 備品を今借りている人(貸与中のときだけ入る)。 */
export type EquipmentHolder = {
  loan_id: number;
  employee_id: number;
  employee_name: string;
  emp_code: string;
  employee_email: string;
  lent_at: string | null;
};

export type Equipment = {
  id: number;
  category: string;
  asset_no: string;
  name: string;
  serial: string;
  /** 紐づいた MDM の端末(製造番号一致で自動、または台帳から手で選ぶ。無ければ null) */
  device_id: number | null;
  /** 紐づけ方。auto=製造番号一致 / manual=手で選んだ(製造番号が違っても自動では外れない) */
  device_link_mode: "auto" | "manual";
  status: string;
  note: string;
  /** 所有区分。owned=自社 / rental=レンタル */
  ownership: string;
  rental_vendor: string;
  rental_start: string | null;
  rental_end: string | null;
  maker: string;
  color: string;
  phone_number: string;
  /** 用途端末(管理端末・踏み台など)の説明 */
  purpose: string;
  /** ロック解除番号。hr:admin 以外には空文字で返る */
  unlock_code: string;
  created_at: string | null;
  updated_at: string | null;
  holder: EquipmentHolder | null;
};

/** 登録で送る値。区分と管理番号は登録したら変えられない。 */
export type EquipmentInput = {
  category: string;
  asset_no: string;
  name?: string;
  serial?: string;
  status?: string;
  note?: string;
  ownership?: string;
  rental_vendor?: string;
  rental_start?: string | null;
  rental_end?: string | null;
  maker?: string;
  color?: string;
  phone_number?: string;
  purpose?: string;
  unlock_code?: string;
};

/** 更新で送る値。渡さなかった項目は変えない。契約日は "" で消す。 */
export type EquipmentPatch = {
  name?: string;
  serial?: string;
  status?: string;
  note?: string;
  ownership?: string;
  rental_vendor?: string;
  rental_start?: string;
  rental_end?: string;
  maker?: string;
  color?: string;
  phone_number?: string;
  purpose?: string;
  unlock_code?: string;
};

/** 申請スナップショットの「名指し返却」。 */
export type ReturnLoanSnapshot = {
  loan_id: number;
  item: string;
  section: string;
  equipment_id: number | null;
  asset_no: string;
};

// ---- 月次人員棚卸(入退場者管理表)--------------------------------------------

/** 2段ヘッダーの1行目。親なし列は rowspan=2 で2段ぶち抜き。 */
export type MonthlyTop = { name: string; colspan: number; rowspan: number };

/** 表の1セル。入力欄は text、SaaS 項目・工程は status(空 = 不要)。 */
export type MonthlyCell = { kind: string; text: string; status: string };

export type MonthlyRow = {
  no: number;
  request_id: number;
  employee_id: number;
  name: string;
  email: string;
  department: string;
  employment_type: string;
  start_date: string;
  cells: MonthlyCell[];
  total: number;
  done: number;
  pct: number;
  state: string;
  state_label: string;
};

export type MonthlyTable = {
  template: string;
  template_id: number | null;
  is_default: boolean;
  top: MonthlyTop[];
  sub: TemplateColumn[];
  columns: TemplateColumn[];
  rows: MonthlyRow[];
};

export type MonthlyKind = {
  kind: string;
  label: string;
  date_label: string;
  tables: MonthlyTable[];
};

export type MonthlyOut = {
  month: string;
  prev_month: string;
  next_month: string;
  head: string[];
  /** 状態 → 表示文字("" は「不要」)。未完了は status_undone */
  status_text: Record<string, string>;
  status_undone: string;
  kinds: MonthlyKind[];
};

// ---- カタログ・割当(core が持つ共通データ。書き込みは人事)-------------------

export type Partner = {
  id: number;
  name: string;
  contact_email: string;
  active: boolean;
  note: string;
};

export type PartnerInput = {
  name?: string;
  contact_email?: string;
  active?: boolean;
  note?: string;
};

export type CatalogItem = {
  id: number;
  name: string;
  fulfillment: string;
  fulfillment_label: string;
  connector: string;
  default_owner: string;
  partner_id: number | null;
  partner_name: string;
  active: boolean;
  note: string;
};

export type CatalogItemInput = {
  name?: string;
  fulfillment?: string;
  connector?: string;
  default_owner?: string;
  partner_id?: number | null;
  active?: boolean;
  note?: string;
};

export type Assignment = {
  id: number;
  employee_id: number;
  employee_name: string;
  item_id: number;
  item_name: string;
  desired: string;
  updated_at: string | null;
};

// ---- WF テンプレート(人事)---------------------------------------------------

export type TemplateSection = { id: number; name: string; sort: number };

export type TemplateField = {
  id: number;
  section_id: number | null;
  section: string;
  name: string;
  ftype: string;
  options: string[];
  required: boolean;
  sort: number;
  /** 転記元。"extra:<key>" = 対象者のカスタム項目の値を最初から入れる(申請者が直せる)。
   *  "emp:<列>" = 従業員マスタの列をサーバーが申請時に埋める(入力欄は出さない)。空は手入力のみ */
  source: string;
  /** 選択肢を借りているカスタム項目の表示名。選択の欄の転記元が選択式のときだけ入り、options はその項目のもの */
  options_from: string;
};

export type TemplateItem = {
  id: number;
  item_id: number;
  name: string;
  fulfillment: string;
  connector: string;
  default_owner: string;
  partner_id: number | null;
  section_id: number | null;
  section: string;
  required: boolean;
  sort: number;
};

export type TemplateStep = {
  id: number;
  section_id: number | null;
  section: string;
  name: string;
  fulfillment: string;
  default_owner: string;
  partner_id: number | null;
  required: boolean;
  sort: number;
  loan: boolean;
  action: string;
};

/** 月次表の2段ヘッダー用の列(申請時にスナップショットされる)。 */
export type TemplateColumn = {
  section: string;
  kind: string;
  name: string;
  key?: string;
  item_id?: number;
  step_id?: number;
};

export type WfTemplate = {
  id: number;
  name: string;
  kind: string;
  note: string;
  is_default: boolean;
  sections: TemplateSection[];
  fields: TemplateField[];
  items: TemplateItem[];
  steps: TemplateStep[];
  columns: TemplateColumn[];
};

export type WfDefaults = {
  default_templates: Record<string, number>;
  required_approvals: number;
};

// ---- 申請・承認・タスク・社外依頼 --------------------------------------------

/** 1件の SLA(server/app/hr/sla.py の event_sla)。over なら超過。 */
export type Sla = {
  kind: string;
  limit_hours: number;
  done: boolean;
  elapsed_hours: number;
  over: boolean;
  remaining_hours: number;
};

export type WfDecision = {
  id: number;
  decided_by: string;
  decision: string;
  reason: string;
  created_at: string;
};

export type WfRequest = {
  id: number;
  kind: string;
  kind_label: string;
  employee_id: number;
  employee_name: string;
  employee_status: string;
  template_id: number | null;
  template_name: string;
  status: "pending" | "approved" | "rejected" | "withdrawn";
  required: number;
  approved_count: number;
  approvals: string;
  approved_by: string[];
  requested_by: string;
  /** 申請ごとの承認者の指定(申請時の写し)。null なら hr:approver なら誰でも */
  approver: WfApprover | null;
  decided_by: string;
  decided_at: string | null;
  reason: string;
  created_at: string;
  start_date: string;
  saas: string[];
  steps: string[];
  task_total: number;
  task_done: number;
  sla: Sla;
};

export type WfApprover = {
  kind: string;
  id: number | null;
  label: string;
  names: string[];
  /** 承認できる人のメール(小文字)。ログインユーザーのメールと突き合わせる */
  emails: string[];
};

/** 承認者の指定に該当する人(申請フォームの下見。GET /api/hr/wf/approvers) */
export type WfApproverPreview = {
  kind: string;
  id: number;
  label: string;
  members: { employee_id: number; name: string; email: string; has_login: boolean }[];
};

/** 回収タスクに添える貸与品 1 行(台帳 loans の抜き書き)。 */
export type TaskLoan = {
  id: number;
  item: string;
  section: string;
  equipment_id: number | null;
  asset_no: string;
  equipment_name: string;
  /** owned / rental。個体が決まっていない行は空 */
  ownership: string;
  ownership_label: string;
  lent_at: string | null;
  returned_at: string | null;
  note: string;
};

export type WfTask = {
  id: number;
  /** 申請から生まれたタスクだけ入る。動的グループ・OU 由来は null */
  request_id: number | null;
  request_kind: string;
  /** OU 同期のタスクは誰にも紐づかないので null */
  employee_id: number | null;
  employee_name: string;
  item_id: number | null;
  external_request_id: number | null;
  name: string;
  section: string;
  action: string;
  action_label: string;
  mode: string;
  status: string;
  status_label: string;
  owner: string;
  detail: string;
  result: string;
  loan: boolean;
  /** 返却タスクが閉じる台帳の行(名指し返却のときだけ入る) */
  loan_id: number | null;
  /** 回収タスクに添える貸与品。回収でなければ null、貸与が無ければ []。完了後は「このタスクで返した行」 */
  loans: TaskLoan[] | null;
  /** 自動で流す操作(group.add / group.remove / ou.move / ou.sync / contacts.unsync)。申請由来は空 */
  op: string;
  op_label: string;
  connector: string;
  /** 操作の相手(グループのアドレス・OU の経路) */
  target: string;
  /** 誰の何が引き金で生まれたか(「従業員編集」など) */
  trigger: string;
  /** 何に割り当てられて生まれたか(dyn_group / org_ou)。申請由来は空 */
  assignment_kind: string;
  assignment_kind_label: string;
  /** 実行予定日。これが来るまで送出しない */
  scheduled_on: string;
  permission_set_id: number | null;
  group_id: number | null;
  dispatched_at: string | null;
  completed_by: string;
  completed_at: string | null;
  created_at: string;
  sla: Sla;
};

export type WfExternalRequest = {
  id: number;
  request_id: number;
  request_kind: string;
  employee_id: number;
  employee_name: string;
  partner_id: number;
  partner_name: string;
  partner_email: string;
  action: string;
  action_label: string;
  items: string[];
  subject: string;
  body: string;
  status: string;
  status_label: string;
  reported_note: string;
  reported_at: string | null;
  sent_at: string | null;
  token_expires_at: string | null;
  send_unknown: boolean;
  created_at: string;
};

export type MailLogRow = {
  id: number;
  external_request_id: number | null;
  to_addr: string;
  subject: string;
  status: string;
  error: string;
  created_at: string;
};

/** 申請の明細。payload は申請時のスナップショット(後からテンプレを直しても変わらない)。 */
export type WfRequestDetail = WfRequest & {
  payload: {
    template_name?: string;
    employee?: Record<string, unknown>;
    fields?: Record<string, string>;
    saas?: string[];
    saas_meta?: { name: string; action: string; fulfillment?: string }[];
    steps?: { name: string; section: string; action: string; fulfillment: string }[];
    columns?: TemplateColumn[];
    return_loans?: ReturnLoanSnapshot[];
    changes?: Record<string, unknown>;
    note?: string;
    [k: string]: unknown;
  };
  decisions: WfDecision[];
  tasks: WfTask[];
  external_requests: WfExternalRequest[];
  /** 取り下げと同時に従業員の登録も消せるか(承認待ちの入社申請で、対象者が入社前・
   *  この申請以外に何も無いときだけ true) */
  employee_deletable: boolean;
  withdrawable: boolean;
};

/** 従業員の登録と一緒に出す入社申請(kind・employee_id はサーバーが決める)。 */
export type OnboardingRequestInput = Omit<
  WfRequestInput,
  "kind" | "employee_id" | "remove_items" | "return_loan_ids" | "changes"
>;

/** 登録前の重複の下見で見つかった従業員。 */
export type SimilarEmployee = {
  id: number;
  emp_code: string;
  name: string;
  email: string;
  status: string;
  status_label: string;
  /** 進行中の入社申請の番号(無ければ null) */
  open_onboard_request_id: number | null;
  match: "name" | "email" | "both";
};

/** 申請フォームが送る値。kind ごとに使う項目が違う。 */
export type WfRequestInput = {
  kind: string;
  employee_id: number;
  template_id?: number | null;
  items?: number[];
  remove_items?: number[];
  steps?: number[];
  fields?: Record<string, string>;
  /** 名指しで返す貸与品台帳の行(変更申請だけ) */
  return_loan_ids?: number[];
  start_date?: string;
  changes?: { org_unit_id?: number | null; position_id?: number | null; employment_type?: string };
  /** 承認者の指定(個人 / 組織 / 動的グループ)。省くと hr:approver なら誰でも */
  approver?: { kind: string; id: number };
};

/** この従業員が今どの種別を申請できるか(画面のボタンの出し分け)。 */
export type EmployeeWfStatus = {
  employee_id: number;
  status: string;
  kinds: Record<string, { can_request: boolean; open_request_id: number | null }>;
};

export type SlaSummary = {
  month: string;
  limits: Record<string, number>;
  kinds: Record<
    string,
    {
      label: string;
      limit_hours: number;
      total: number;
      done: number;
      met: number;
      rate: number | null;
      open: number;
    }
  >;
  /** その月に作られた申請の件数(種別ごと + total) */
  requests: Record<string, number>;
  request_labels: Record<string, string>;
  tasks: { total: number; done: number; failed: number; pending: number };
  automation: { done: number; auto_done: number; rate: number | null };
  lead_time_seconds: number | null;
  overdue_count: number;
  overdue: {
    kind: string;
    id: number;
    request_id: number;
    label: string;
    status: string;
    elapsed_hours: number;
    limit_hours: number;
  }[];
};

/** ログイン無しで見せる完了報告(トークンは URL にだけある)。 */
export type PublicReport = {
  id: number;
  partner_name: string;
  employee_name: string;
  subject: string;
  action_label: string;
  reported: boolean;
};

// ---- 社外依頼メールの雛形(HR3 C2)--------------------------------------------

/** 向き(provision / deprovision)× 言語(ja / en)の入れ子。 */
export type ExternalMailTexts = Record<string, Record<string, { subject: string; body: string }>>;

export type ExternalMailSettings = {
  external_mail: ExternalMailTexts;
  /** 組ごとに「既定のままか」。false なら人が直している */
  is_default: Record<string, Record<string, boolean>>;
  placeholders: { key: string; label: string }[];
  subject_max: number;
  body_max: number;
};

// ---- 動的グループ・権限セット(HR3 C3 / C4)------------------------------------

/** ルールの条件 1 行。値の型は field と cmp で決まる(サーバが同じ規則で弾く)。 */
export type GroupCond = {
  field: string;
  cmp: string;
  value: number | string | string[];
};

/** ルールの入れ子。一番外側は必ずこの形。 */
export type GroupRuleNode = {
  op: "and" | "or";
  conds: (GroupCond | GroupRuleNode)[];
};

export type DynGroup = {
  id: number;
  name: string;
  description: string;
  rule: GroupRuleNode | Record<string, never>;
  active: boolean;
  member_count: number;
  permission_set_count: number;
  created_at: string;
  updated_at: string;
};

export type DynGroupInput = {
  name?: string;
  description?: string;
  rule?: GroupRuleNode;
  active?: boolean;
};

export type GroupMember = {
  id: number;
  emp_code: string;
  name: string;
  email: string;
  department: string;
  position: string;
  status: string;
};

export type GroupEvent = {
  id: number;
  kind: string;
  employee_id: number;
  employee_name: string;
  trigger: string;
  effective_on: string;
  at: string;
};

/** クエリビルダーの選択肢。**画面はここから作る**(field や上限を埋め込まない)。 */
export type GroupFields = {
  fields: { key: string; label: string; cmps: { key: string; label: string }[] }[];
  org_units: { id: number; name: string; parent_id: number | null; kind: string; active: boolean }[];
  positions: { id: number; name: string; rank: number; active: boolean }[];
  employment_types: string[];
  statuses: string[];
  limits: { depth: number; conds: number; emails: number };
};

export type GroupPreview = {
  count: number;
  members: GroupMember[];
  /** 先頭だけ返っている(count は全件) */
  truncated: boolean;
};

export type PermissionSet = {
  id: number;
  group_id: number;
  name: string;
  kind: string;
  kind_label: string;
  target: string;
  active: boolean;
  note: string;
  connector: string;
  /** コネクタが設定済みか。false なら手作業のタスクになる */
  configured: boolean;
  pending_tasks: number;
  /** 外部に配り終えている人数(台帳 `permission_grants` の行数) */
  granted_count: number;
  created_at: string;
  updated_at: string;
  /** 作成・更新のときだけ返る注意書き */
  warning?: string;
};

/** 初期同期・全剥奪の結果。`shared` / `transferred` は**外部に触らず台帳だけ**動かした数 */
export type GroupSyncResult = {
  created: number;
  skipped: number;
  cancelled: number;
  dispatched: number;
  shared: number;
  transferred: number;
};

export type PermissionSetInput = {
  name?: string;
  kind?: string;
  target?: string;
  note?: string;
  active?: boolean;
};

// ---- お知らせ(suite 共通)-----------------------------------------------------

export type NotificationRow = {
  id: number;
  kind: string;
  /** 種別の日本語。core が単一ソース(app/core/notifications.py) */
  kind_label: string;
  title: string;
  body: string;
  link: string;
  app: string;
  user_sub: string | null;
  read_at: string | null;
  created_at: string;
};

export type NotificationPage = Page<NotificationRow> & { unread: number };

// ---- HR取込・匿名化・保有期間(R2 ブロック4)---------------------------------

/** 1行の判定。status は add / update / same / error。 */
export type ImportRow = {
  line: number;
  emp_code: string;
  name: string;
  email: string;
  status: string;
  error: string;
  diffs: string[];
  id: number | null;
};

export type ImportResult = {
  dry_run: boolean;
  added: number;
  updated: number;
  unchanged: number;
  errors: number;
  rows: ImportRow[];
  /** ファイル全体に対する注意書き(旧形式の氏名を割ったときなど) */
  warnings: string[];
  /** 判定のときだけ返る引換券(反映に使う。期限30分・1回だけ) */
  token: string;
};

export type AnonymizeCandidate = {
  id: number;
  emp_code: string;
  name: string;
  email: string;
  department: string;
  left_at: string | null;
  offboard_done_at: string | null;
  days_elapsed: number | null;
  retention_due: boolean;
  blocked: string;
};

export type AnonymizeCandidates = {
  items: AnonymizeCandidate[];
  total: number;
  retention_days: number;
  max_days: number;
};

export type RetentionRun = {
  days: number;
  anonymized: number;
  employee_ids: number[];
  skipped: string;
};

/** 全アプリ共通の従業員(読み取り専用。server/app/core/directory の EmployeeBriefOut)。 */
export type EmployeeBrief = {
  id: number;
  emp_code: string;
  name: string;
  email: string;
  status: string;
  employment_type: string;
  direct: boolean;
  org_unit_id: number | null;
  org_unit_path: string;
};

// ---- デバイス管理(R4)--------------------------------------------------------

/** デバイス台帳の1行(server/app/mdm/schemas.py の DeviceOut と同じ形)。 */
export type Device = {
  id: number;
  hostname: string;
  serial: string;
  os: string;
  os_version: string;
  model: string;
  employee_email: string;
  employee_id: number | null;
  employee_name: string;
  /** 利用者を画面で確定したか。false はチェックインで名乗ったメールから引いただけ(グループ指定に使わない)。 */
  employee_confirmed: boolean;
  source: string;
  app_count: number;
  policy_applied: number;
  policy_total: number;
  last_seen: string | null;
  created_at: string | null;
  updated_at: string | null;
};

/** インベントリのアプリ1件。エージェントが送る形なので緩く受ける。 */
export type DeviceApp = { name?: string; version?: string };

export type DeviceDetail = Device & {
  inventory: { apps?: DeviceApp[] } & Record<string, unknown>;
  policy_state: Record<string, string>;
};

/** 配る対象(AG2)。3 つは「または」。何も選んでいなければ配らない。 */
export type RegistryTarget = { all: boolean; groups: number[]; devices: number[] };

/** 配布するレジストリ1件。エージェントへは target を除いて渡る。 */
export type RegistryPolicy = {
  hive: string;
  path: string;
  name: string;
  type: "dword" | "string";
  value: string | number;
  /** HKCU の当て先。無ければ logged_on。HKLM の行には付かない */
  users?: RegistryUsers;
  target: RegistryTarget;
};

/** HKCU の当て先: ログオン中のユーザー / 管理者以外の全アカウント */
export type RegistryUsers = "logged_on" | "non_admins";

export type RegistryPolicies = {
  policies: RegistryPolicy[];
  updated_at: string | null;
  /** 対象に出てくる端末の id → ホスト名 */
  device_names: Record<string, string>;
};

/** 当たる理由: all / device / group:<id>。当たらなければ "" */
export type RegistryReason = string;
export type RegistryPreview = { counts: number[]; device: RegistryReason[] | null };
export type RegistryGroup = { id: number; name: string; active: boolean; members: number };
export type RegistryTemplate = {
  key: string;
  title: string;
  note: string;
  source: string;
  policies: Omit<RegistryPolicy, "target">[];
};
export type DeviceRegistryPolicy = RegistryPolicy & { reason: RegistryReason };

/** Windows エージェントの配布物(AG1)。api イメージに焼き込んだ exe。 */
export type AgentPackage = {
  available: boolean;
  filename: string;
  version: string;
  sha256: string;
  size: number;
  download_path: string;
  /** 自己更新の署名(.sig)が付いているか。無いと端末は自己更新しない。 */
  signed: boolean;
};

/** 登録コード。平文(code)は発行の応答にだけ入る。 */
export type EnrollCode = {
  id: number;
  prefix: string;
  memo: string;
  expires_at: string;
  max_uses: number;
  used_count: number;
  created_by: string;
  created_at: string | null;
  revoked_at: string | null;
  state: "active" | "expired" | "used_up" | "revoked";
  /** 端末指定(入れ直し用)のコードなら、その端末。 */
  device_id: number | null;
  /** 失効の応答にだけ入る。巻き添えで失効させた未紐付けの端末トークンの数。 */
  revoked_tokens?: number;
};
export type EnrollCodeIssued = EnrollCode & { code: string };

/** 端末トークン(頭の数文字だけ。平文はサーバーも持っていない)。 */
export type DeviceAgentToken = {
  id: number;
  prefix: string;
  device_id: number | null;
  enroll_code_id: number | null;
  hostname: string;
  serial: string;
  created_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  revoked_by: string;
};

/** Apple MDM の登録1件(server/app/mdm/schemas.py の EnrollmentOut と同じ形)。 */
export type MdmEnrollment = {
  id: number;
  udid: string;
  device_id: number | null;
  serial: string;
  topic: string;
  token_present: boolean;
  enrolled_at: string | null;
  last_seen: string | null;
  checked_out_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** `macos` / `ios` / `ipados` / `unknown`(名乗りが読めない。読むだけのコマンドしか出さない) */
  platform?: string;
  /** 監視モードか。`null` は未確認(DeviceInformation の応答待ち) */
  supervised?: boolean | null;
  /** パスコード解除に使う UnlockToken を持っているか(値そのものは返らない) */
  unlock_token_present?: boolean;
};

/** その端末に出せるコマンド(サーバーが OS と監視モードで絞った結果)。 */
export type MdmRequestType = {
  request_type: string;
  role: string;
  destructive: boolean;
  available: boolean;
  /** `available: false` の理由(画面にそのまま出す) */
  reason: string;
};

/**
 * 端末に送ったコマンド1件。`status` は
 * `queued`(積んだだけ)/ `pushed`(NanoMDM が受けた)/ `push_failed`(**届いていない**)/
 * `removed`(Android の初期化が効いて**管理から外れた**)/
 * `acknowledged` / `error` / `notnow`(端末が今は無理と答えた)。
 */
export type MdmCommand = {
  id: number;
  /** `apple` / `android`。同じ表に載っているので履歴を混ぜない目印 */
  platform?: string;
  udid: string;
  command_uuid: string;
  request_type: string;
  status: string;
  payload: Record<string, unknown>;
  response: Record<string, unknown>;
  error: string;
  created_by: string;
  created_at: string | null;
  updated_at: string | null;
};

/** 端末のコマンド履歴。**未登録でも 404 にならず `enrollment: null` が返る。** */
export type DeviceCommands = {
  items: MdmCommand[];
  total: number;
  enrollment: MdmEnrollment | null;
  request_types?: MdmRequestType[];
};

/** Webhook の受け口の件数(`server/app/mdm/schemas.py` の WebhookStatusOut と同じ形)。 */
export type MdmWebhookCounts = {
  ok: number;
  duplicate: number;
  ignored: number;
  error: number;
  ignored_reasons: { key: string; label: string; count: number }[];
};

export type MdmWebhookStatus = {
  generated_at: string;
  window_hours: number;
  signing_key_set: boolean;
  recent: MdmWebhookCounts;
  total: MdmWebhookCounts;
};

/**
 * Android MDM(Android Management API)。`server/app/mdm/schemas.py` の
 * `Android*Out` と同じ形。**SA の鍵は伏字(`•••設定済み`)で返る。**
 */
export type AndroidConfig = {
  key: string;
  value: Record<string, unknown>;
  updated_at: string | null;
  /** 企業(`enterprises/LC0xxxx`)に紐付いているか。紐付け前は画面の出すものが変わる */
  bound: boolean;
  enterprise: string;
};

export type AndroidSignupUrl = { name: string; url: string };

export type AndroidBindResult = {
  enterprise: string;
  display_name: string;
  default_policy: string;
};

export type AndroidTestResult = {
  ok: boolean;
  enterprise: string;
  display_name: string;
  error: string;
};

/** ポリシーのテンプレ(仕事用プロファイル / フル管理 / キオスク)。 */
export type AndroidTemplate = {
  id: string;
  label: string;
  hint: string;
  body: Record<string, unknown>;
};

export type AndroidPolicy = {
  policy_id: string;
  name: string;
  template: string;
  body: Record<string, unknown>;
  updated_by: string;
  created_at: string | null;
  updated_at: string | null;
};

export type AndroidPolicies = { items: AndroidPolicy[]; templates: AndroidTemplate[] };

/**
 * 登録トークンの控え。**`value` と `qr_code` は発行の戻りだけ**に入る
 * (保存していないので一覧には出てこない。QR はその場で控える)。
 */
/** 端末側の入り方(`app/mdm/android/enroll.py` の `setup_guide`)。文言はサーバー側。 */
export type AndroidSetupGuide = {
  /** 私用あり(仕事用プロファイル)。**初期化が要らないのはこちらだけ** */
  work_profile: boolean;
  title: string;
  steps: string[];
};

export type AndroidToken = {
  id: number;
  name: string;
  policy_id: string;
  employee_email: string;
  one_time_only: boolean;
  allow_personal_usage: string;
  setup: AndroidSetupGuide;
  expires_at: string | null;
  expired: boolean;
  created_by: string;
  created_at: string | null;
  value?: string;
  qr_code?: string;
};

export type AndroidTokens = { items: AndroidToken[]; total: number };

export type AndroidSyncResult = {
  devices: number;
  created: number;
  updated: number;
  ignored: number;
  resolved: number;
  skipped: string;
  error: string;
  /** 一覧が途中までだった回(クォータ / ページ打ち切り / 台数上限)。消失判定はしない。 */
  partial?: boolean;
  /** 台数が取り込みの上限を超えた。 */
  truncated?: boolean;
};

/** 台帳の1行と AMAPI の端末の対応(`mdm_android_devices`)。 */
export type AndroidDeviceLink = {
  id: number;
  device_name: string;
  device_id: number | null;
  state: string;
  enrollment_token_name: string;
  last_sync_at: string | null;
};

/** Android のコマンド履歴。**未登録でも 404 にならず `device: null` が返る。** */
export type AndroidDeviceCommands = {
  items: MdmCommand[];
  total: number;
  device: AndroidDeviceLink | null;
};

/** mdm が扱う設定(`agent` / `mdm` の2キーだけ)。秘密は伏字で返る。 */
export type MdmSetting = {
  key: string;
  value: Record<string, unknown>;
  updated_at: string | null;
};

// ---- ID管理(R5)--------------------------------------------------------------

/** 突合の実行履歴の1行(server/app/idp/schemas.py の RunOut と同じ形)。 */
export type ReconcileRun = {
  id: number;
  started_at: string | null;
  finished_at: string | null;
  trigger: string;
  actor: string;
  findings: number;
  new_count: number;
  resolved_count: number;
};

/**
 * カタログ項目1件ぶんの指摘。キーは指摘の種類(日本語。`app/idp/constants.py` の CAT_*)で、
 * 値はメールの配列。照会できなかったときは種類の代わりに `status` が入る。
 */
export type ReconcileSaasRow = {
  mode: string;
  connector: string;
  item_id: number | null;
  status?: string;
} & Record<string, unknown>;

export type ReconcileReport = {
  at?: string;
  findings?: number;
  saas?: Record<string, ReconcileSaasRow>;
};

export type ReconcileRunDetail = ReconcileRun & {
  first: boolean;
  previous_id: number | null;
  new: string[];
  resolved: string[];
  finding_keys: string[];
  report: ReconcileReport;
  notification_id: number | null;
};

/** コネクタ1つ。`items` はそのコネクタを使うカタログ項目の名前。 */
export type IdpConnector = {
  key: string;
  label: string;
  mode: string;
  mode_label: string;
  configured: boolean;
  items: string[];
};

export type ConnectorTestResult = { ok: boolean; detail: string };

/** 初期パスワードの再発行の結果。**この応答 1 回だけ**(サーバーには残らない)。 */
export type ResetInitialPasswordResult = {
  email: string;
  password: string;
  change_at_next_login: boolean;
};

/** idp が扱う設定(`gws` / `keycloak` / `reconcile` の3キーだけ)。秘密は伏字で返る。 */
export type IdpSetting = {
  key: string;
  value: Record<string, unknown>;
  updated_at: string | null;
};

export type FulfillResult = {
  events: number;
  done: number;
  failed: number;
  skipped: number;
};

// ---- サービスプロバイダー(R7a)-----------------------------------------------

/** SAML の属性設定の1行(SAML 属性名 ↔ ユーザー項目)。 */
export type SamlAttributeRow = { saml_name: string; user_field: string };

/**
 * フォームの値。種別ごとに使う項目が違うので緩い型にする
 * (真実は `server/app/idp/sp_service.py` の normalize)。
 */
export type ProviderForm = {
  name: string;
  display_name?: string;
  login_url?: string;
  portal_visible?: boolean;
  enabled?: boolean;
  logo?: string;
  sort_order?: number;
  // SAML
  sp_entity_id?: string;
  acs_url?: string;
  extra_acs_urls?: string[];
  name_id_attribute?: string;
  name_id_format?: string;
  relay_state?: string;
  signature_algorithm?: string;
  authn_context?: string;
  sso_url_name?: string;
  session_max_hours?: number;
  signing_certificate?: string;
  attributes?: SamlAttributeRow[];
  // OIDC
  client_id?: string;
  redirect_uris?: string[];
  pkce?: string;
  post_logout_url?: string;
};

/** 一覧の1行。`status` は「有効 / 無効 / Keycloakに無い / 不明」。 */
export type ServiceProvider = {
  id: number;
  kind: string;
  kind_label: string;
  name: string;
  display_name: string;
  kc_client_id: string | null;
  kc_client_uuid: string | null;
  login_url: string;
  portal_visible: boolean;
  enabled: boolean;
  logo: string;
  sort_order: number;
  status: string;
  secret: string | null;
  created_at: string | null;
  updated_at: string | null;
};

/** SP 側に登録してもらう IdP の値。URL は公開 URL で組み立てられている。 */
export type IdpInfo = {
  issuer: string;
  sso_url: string;
  logout_url: string;
  direct_login_url: string | null;
  metadata_url: string;
  realm: string;
  configured: boolean;
};

export type ServiceProviderDetail = ServiceProvider & {
  form: ProviderForm;
  idp_info: IdpInfo;
};

/** ポータルの「サービス」節のタイル。 */
export type PortalTile = {
  id: number;
  kind: string;
  kind_label: string;
  display_name: string;
  logo: string;
  url: string;
};

// ---- ユーザー管理・ドメイン設定・API クライアント(R7b)-------------------------
// 正は Keycloak。ここに出てくる値は `server/app/idp/users_service.py` などが
// Keycloak から組み立てたもので、スイートの表に持つのは最終ログインだけ。

/** ユーザー一覧の1行。 */
export type IdpUser = {
  id: string;
  username: string;
  displayName: string;
  email: string;
  enabled: boolean;
  status: string;
  lastLogin: string | null;
  /** 効いているアクセスポリシーの名前(R7d)。割当が無ければ既定の名前が入る。 */
  policyName: string;
  /** 上の名前が「既定だから効いている」ものかどうか。 */
  policyIsDefault: boolean;
};

/** ユーザー詳細(Drawer のタブ5つぶん)。 */
export type IdpUserDetail = IdpUser & {
  profile: {
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    emailVerified: boolean;
    createdAt: string | null;
    attributes: Record<string, string>;
  };
  password: {
    set: boolean;
    updatedAt: string | null;
    expiresAt: string | null;
    expiryDays: number;
    method: string;
  };
  access: {
    enabled: boolean;
    /** 直接割当(編集フォームが送り返すのはこれだけ) */
    directRoles: string[];
    /** グループ・複合ロール込みの実効ロール(表示専用) */
    effectiveRoles: string[];
    /** effectiveRoles - directRoles。読み取り専用で出す */
    inheritedRoles: string[];
  };
  accessInfo: {
    lastLogin: string | null;
    sessions: number;
    failureCount: number;
    lastFailureAt: string | null;
    lastFailureIp: string;
    locked: boolean;
  };
  otp: { registered: boolean; count: number; registeredAt: string | null };
  /** アクセスポリシーの割当(R7d)。 */
  policy: PolicyAssignment;
};

/** 作成・更新で送る値。更新は入れたキーだけ直す。 */
export type IdpUserForm = {
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  enabled?: boolean;
  roles?: string[];
  attributes?: Record<string, string>;
  generate?: boolean;
  password?: string;
  temporary?: boolean;
};

/** 初期パスワードの戻り。メールで送れたら `password` は空。 */
export type IdpPasswordResult = {
  id?: string;
  username: string;
  password: string;
  passwordDelivered: boolean;
  passwordNote: string;
  temporary: boolean;
};

export type IdpUserCreated = IdpUser & IdpPasswordResult & { roles: string[] };

/** CSV 取込の行ごとの結果(パスワードは入らない)。 */
export type BulkRow = { line: number; username: string; result: string; message: string };

/** 取込の受付(本実行は 202 で `status: running`。結果はジョブ詳細を見る)。 */
export type BulkResult = {
  id: number | null;
  dry_run: boolean;
  mode: string;
  kind: string;
  status: string;
  total: number;
  processed: number;
  ok: number;
  failed: number;
  undelivered: number;
  rows: BulkRow[];
};

export type BulkJob = {
  id: number;
  kind: string;
  kindLabel: string;
  status: string;
  statusLabel: string;
  actor: string;
  filename: string;
  total: number;
  processed: number;
  ok: number;
  failed: number;
  message: string;
  createdAt: string | null;
  finishedAt: string | null;
  rows?: BulkRow[];
};

// ---- ログ(R7c)---------------------------------------------------------------

/** Keycloak のイベントの写し(認証ログ・ユーザー操作ログの 1 行)。 */
export type LogEvent = {
  id: number;
  occurredAt: string;
  username: string;
  userId: string;
  ip: string;
  eventType: string;
  eventTypeLabel: string;
  result: string;
  resultLabel: string;
  error: string;
  clientId: string;
  /** SSO TARGET。SP の表示名に解決できていればその名前、無ければ clientId */
  target: string;
  sessionId: string;
  details: Record<string, string>;
};

/** 管理者操作ログ。スイートの監査と Keycloak の管理操作が混ざる。 */
export type LogAdminOp = {
  id: string;
  occurredAt: string;
  source: "suite" | "keycloak";
  sourceLabel: string;
  operation: string;
  action: string;
  target: string;
  actor: string;
  app: string;
  ip: string;
  result: string;
  detail: string;
};

/** 同期ログ(突合の実行履歴 + 自動タスクの監査)。 */
export type LogSync = {
  id: string;
  occurredAt: string;
  task: string;
  trigger: string;
  actor: string;
  result: string;
  resultLabel: string;
  count: number | null;
  message: string;
  runId: number | null;
};

/** 取込の状態(画面の帯)。件数と時刻だけで、イベントの中身は入らない。 */
export type LogStatus = {
  enabled: boolean;
  manageKcConfig: boolean;
  kcExpirationDays: number;
  retentionDays: number;
  counts: Record<string, number>;
  cursors: Record<string, { lastEventAt: string | null; updatedAt: string | null }>;
  lastImportAt: string | null;
  keycloak: {
    known: boolean;
    eventsEnabled: boolean;
    adminEventsEnabled: boolean;
    eventsExpirationDays: number;
  };
};

export type LogSettings = {
  enabled: boolean;
  manage_kc_config: boolean;
  kc_expiration_days: number;
  retention_days: number;
};

export type LogPullResult = {
  login: { inserted: number; fetched: number; complete: boolean };
  admin: { inserted: number; fetched: number; complete: boolean };
};

/** カスタム属性(Keycloak のユーザープロファイル)。 */
export type CustomAttr = {
  name: string;
  displayName: string;
  required: boolean;
  permissions: { edit?: string[]; view?: string[] };
};

export type CustomAttrs = { items: CustomAttr[]; total: number; max: number };

/** ドメイン設定のフォーム(RealmRepresentation との相互変換は API 側)。 */
export type DomainForm = {
  page_title: string;
  remember_me: boolean;
  lockout_count: number;
  lockout_minutes: number;
  permanent_lockout: boolean;
  login_with_email: boolean;
  logo: string;
  password_min_length: number;
  /** 複雑度は「何文字以上」。0 でその語を落とす(0〜10)。 */
  password_upper: number;
  password_lower: number;
  password_digit: number;
  password_special: number;
  password_not_username: boolean;
  password_history: number;
  password_expiry_days: number;
  password_self_reset: boolean;
  password_note: string;
  sso_idle_minutes: number;
  sso_max_hours: number;
  timezone: string;
  locale: string;
  otp_issuer: string;
  otp_digits: number;
  otp_period: number;
  /** 端末証明書のインストールメール(OS × 言語)。R7e で増えた鍵。 */
  device_cert_mail: DeviceCertMail;
};

export type DomainSettings = {
  form: DomainForm;
  /** Keycloak の passwordPolicy の原文。 */
  policy: string;
  /** 画面から直せない語(重複・引数が読めない)。あると保存できない。 */
  policy_warnings: string[];
  logo_url: string;
  logo_warning: string;
};

export type DomainUpdated = DomainSettings & { diff: Record<string, [string, string]> };

/** API クライアント(Keycloak のサービスアカウント)。 */
export type ApiClientRow = {
  id: number;
  clientId: string;
  note: string;
  scopes: string[];
  createdBy: string;
  createdAt: string | null;
  lastUsedAt: string | null;
  kcClientUuid: string;
  status: string;
};

export type ApiClientDetail = ApiClientRow & {
  secret: string;
  tokenUrl: string;
  usage: { tokenUrl: string; curl: string; scopes: string[]; note: string };
};

export type ApiClientCreated = ApiClientRow & { secret: string };

/** リクエストのヘッダー。`Content-Type: ""` を渡すと外す(multipart は fetch に任せる)。 */
/** アクセスポリシー(R7d)。値は `server/app/idp/constants.py` と同じ。 */
export type PolicyFlowStatus = {
  known: boolean;
  reason: string;
  spiAvailable: boolean;
  flowExists: boolean;
  /** 実物のフローの形が期待どおりか(旧形・手で触られた状態を出す)。 */
  shapeOk?: boolean;
  browserFlow: string;
  active?: boolean;
  /** realm に登録された外部 IdP の件数。 */
  idpCount?: number;
  /** そのうち Post Login Flow が itmgmt-post-login になっている件数。 */
  idpBound?: number;
  /** itmgmt-post-login の形と要求水準が期待どおりか。 */
  postFlowOk?: boolean;
  /**
   * 外部 IdP から戻る経路にも判定が入っているか。偽のあいだは **その経路が素通り**
   * (Keycloak は IdP の認証後に元のブラウザフローを続けないため)。
   */
  brokeredOk?: boolean;
  /** 標準でも itmgmt-browser でもないブラウザフロー(奪わないので有効化できない)。 */
  foreignBrowserFlow?: string;
  /** 別の Post Login Flow が入っている IdP の別名(同じ理由で有効化できない)。 */
  foreignPostIdps?: string[];
  /**
   * token endpoint の password grant(direct access grants)が有効な client。
   * **この経路は認証フローを通らない**ので、判定を効かせても迂回できる。
   * 奪える設定ではないので数えて出すだけ(Keycloak 側で切る)。
   */
  directGrantClients?: string[];
  /** client を最後まで数えられたか(偽なら「無し」と読ませない) */
  directGrantComplete?: boolean;
  /**
   * realm 自身の「判定を通らない入口」(R7d fix2 の中 5)。どちらもブラウザ
   * フローの外で動くので、真のあいだは有効化できない(`enforceBlockers`)。
   */
  resetPasswordAllowed?: boolean;
  registrationAllowed?: boolean;
};

export type PolicyStatus = {
  /** 要求(効かせたい / 切りたい)。 */
  enforce: boolean;
  /**
   * **再 GET で確かめた** realm の browserFlow(`browser` / `itmgmt-browser` /
   * `unknown`)。空 = 一度も確かめていない。
   */
  applied: string;
  /** 判定 API の共有秘密が api に入っていて、形も使えるか(値は返らない)。 */
  tokenSet: boolean;
  groupCount: number;
  ipGroupCount: number;
  assignedCount: number;
  defaultGroupId: string | null;
  defaultGroupName: string;
  decisionCounts: Record<string, number>;
  keycloak: PolicyFlowStatus;
  /** 空でなければ「有効」に切り替えられない(API も 400 で断る)。 */
  enforceBlockers: string[];
};

export type PolicyAtoms = {
  atoms: { expr: string; note: string }[];
  operators: string[];
  limits: {
    expressionMax: number;
    tokenMax: number;
    tokenCountMax: number;
    depthMax: number;
  };
};

// ---- 電話帳同期(CS1。GWS 連携のオプション)----------------------------------

export type ContactsSetting = { sa_email: string; ops_email: string };
export type ContactRole = "distribute" | "listing";
export type ContactMode = "include" | "exclude";
export type ContactBookMember = {
  employee_id: number;
  role: ContactRole;
  mode: ContactMode;
  name: string;
  emp_code: string;
  email: string;
  status: string;
};
export type ContactBook = {
  id: number;
  name: string;
  label_name: string;
  interval_min: number;
  distribute_group_id: number | null;
  listing_group_id: number | null;
  listing_without_phone: boolean;
  active: boolean;
  last_run_at: string | null;
  members: ContactBookMember[];
};
export type ContactBookInput = Partial<Omit<ContactBook, "id" | "last_run_at" | "members">>;
export type DynGroupBrief = { id: number; name: string; active: boolean; members: number };
export type ContactPreviewPerson = {
  id: number;
  emp_code: string;
  name: string;
  email: string;
  dept: string;
  title: string;
  work_mobile?: string | null;
  work_phone?: string | null;
  extension?: string | null;
};
export type ContactPreview = {
  distribute: number;
  listing: number;
  gws_checked: boolean;
  drops: Record<ContactRole, Record<string, number>>;
  distribute_list: ContactPreviewPerson[];
  listing_list: ContactPreviewPerson[];
  truncated: boolean;
};
/** 撤収の記録(CS1 fix1)。cancelled 以外は配布先から外れている */
export type ContactWithdrawal = {
  employee_id: number;
  task_id: number | null;
  state: "open" | "done" | "closed" | "cancelled";
  state_label: string;
  actor: string;
  updated_at: string;
  name: string;
  emp_code: string;
};
export type ContactSyncRun = {
  id: number;
  trigger: string;
  actor: string;
  started_at: string;
  finished_at: string | null;
  status: "running" | "ok" | "partial" | "failed";
  stats: Record<string, number>;
  error: string;
};

export type IpGroup = {
  id: string;
  displayName: string;
  note: string;
  entries: string[];
  count: number;
  /** この IP グループを名前で参照している条件式の場所(消す前の確認)。 */
  usedBy: string[];
  inUse: boolean;
  createdAt: string | null;
  updatedAt: string | null;
};

export type PolicyTemplate = {
  id: string;
  key: string;
  displayName: string;
  expression: string;
  usedCount: number;
  inUse: boolean;
  createdAt: string | null;
  updatedAt: string | null;
};

export type PolicyGroup = {
  id: string;
  displayName: string;
  isDefault: boolean;
  selfPasswordReset: boolean;
  allowExpr: string;
  otpSkipExpr: string;
  otpSetupExpr: string;
  cookieTtlHours: number;
  assignedCount: number;
  spCount: number;
  createdAt: string | null;
  updatedAt: string | null;
};

/** ポリシーごとの SP の許可(既定は許可。`configured` が false なら行が無い)。 */
export type PolicyGroupSp = {
  id: number;
  name: string;
  displayName: string;
  kind: string;
  clientId: string;
  enabled: boolean;
  allowed: boolean;
  expr: string;
  configured: boolean;
};

export type PolicyGroupDetail = PolicyGroup & { sps: PolicyGroupSp[] };

export type PolicyGroupForm = {
  displayName?: string;
  isDefault?: boolean;
  selfPasswordReset?: boolean;
  allowExpr?: string;
  otpSkipExpr?: string;
  otpSetupExpr?: string;
  cookieTtlHours?: number;
  sps?: { spId: number; allowed: boolean; expr?: string }[];
};

/** ユーザー 1 人ぶんの割当(割当が無ければ既定が「実際に効くもの」)。 */
export type PolicyAssignment = {
  userId: string;
  groupId: string | null;
  groupName: string;
  assignedBy: string;
  assignedAt: string | null;
  defaultGroupId: string | null;
  defaultGroupName: string;
  effectiveGroupName: string;
  effectiveIsDefault: boolean;
};

/** 判定の結果(試算と判定ログで同じ語)。 */
export type PolicyDecision = {
  decision: string;
  otpSetup: boolean;
  reason: string;
  group: string;
};

export type PolicyDecisionRow = {
  id: number;
  occurredAt: string | null;
  decision: string;
  decisionLabel: string;
  username: string;
  userId: string;
  ip: string;
  clientId: string;
  target: string;
  certPresent: boolean;
  otpSetup: boolean;
  reason: string;
  groupId: string | null;      // 手で消されたポリシーの判定は null もありうる
  groupName: string;
};

// ---- 端末証明書(R7e)-------------------------------------------------------------

/** 発行できる OS(表示名は `deviceCertOptions.ts`)。 */
export type DeviceCertOs = "macos" | "windows" | "ios" | "android" | "other";

/**
 * 証明書の状態。**日本語の表示は画面側**(`deviceCertOptions.ts`)。
 * `active` は「1 度でも判定で使われた」= 使用中。`issued` はまだ落としていない。
 */
export type DeviceCertStatus = "issued" | "downloaded" | "active" | "revoked" | "expired";

/** インストールメールの言語。 */
export type DeviceCertLang = "ja" | "en";

/** 一覧・詳細の 1 行。日時は ISO8601 の UTC(表示は `jst()`)。 */
export type DeviceCert = {
  id: string;
  user_id: string;
  username: string;
  os: DeviceCertOs;
  note: string;
  mail_subject_note: string;
  status: DeviceCertStatus;
  serial: string;
  last_used_at: string | null;
  not_after: string;
  revoked_at: string | null;
  revoke_reason: string;
  downloaded_at: string | null;
  download_count: number;
  created_at: string;
  issued_by: string;
  /** ユーザー詳細から入替リクエストが出ている(KPI の `revoke_requests` が数える)。 */
  replace_requested: boolean;
  /** ダウンロード用の一式がまだ生きている(リンクが有効)。 */
  bundle_ready: boolean;
};

/** 一覧の上に出す 6 枚。`issued / limit` は 1 枚にまとめて見せる。 */
export type DeviceCertKpi = {
  issued: number;
  limit: number;
  unused: number;
  expiring_30d: number;
  revoke_requests: number;
  replaceable: number;
  /** 社内 CA 自体の期限。**ここを過ぎると誰もログインできない**。CA が無ければ null。 */
  ca_not_after: string | null;
};

export type DeviceCertList = { items: DeviceCert[]; kpi: DeviceCertKpi };
export type DeviceCertItem = { item: DeviceCert };

/**
 * 発行の応答。**`p12_password` はこの応答にだけ入る**(あとからは読めない)。
 * 画面は state に置くだけで、localStorage にも URL にも入れない。
 */
export type DeviceCertIssued = {
  item: DeviceCert;
  download_url: string;
  p12_password: string;
  bundle_expires_at: string;
  mail_sent: boolean;
};

/** メール再送。**古いリンクは無効になる**。パスワードは再発行されない。 */
export type DeviceCertMailed = {
  download_url: string;
  bundle_expires_at: string;
  mail_sent: boolean;
};

export type DeviceCertEventAction =
  | "issue"
  | "download"
  | "mail"
  | "revoke"
  | "note"
  | "expire"
  | "request_replace";

export type DeviceCertEvent = {
  at: string;
  actor: string;
  action: DeviceCertEventAction;
  detail: string;
};

export type DeviceCertMailText = { subject: string; body: string };
export type DeviceCertMailLangs = Record<DeviceCertLang, DeviceCertMailText>;
/** ドメイン設定の `device_cert_mail`。OS × 言語で件名と本文を持つ。 */
export type DeviceCertMail = Record<DeviceCertOs, DeviceCertMailLangs>;


// ---- DLP(R6c)------------------------------------------------------------------
//
// docs/dlp.md §7 / §10 の契約。サーバー(server/app/dlp/schemas.py)と同じ形。

/** USB ストレージの扱い。端末の `usb_mode` が null なら設定の `usb_mode_default` に従う。 */
export type DlpUsbMode = "allow" | "readonly" | "block";
export type DlpScanStatus = "queued" | "sent" | "running" | "done" | "error" | "cancelled" | "reviewed";
export type DlpScanTrigger = "offboard" | "manual";
export type DlpUsbKind = "attached" | "detached" | "blocked";

export type DlpOverview = {
  devices: { total: number; online_24h: number };
  scans: { queued: number; sent: number; running: number; done: number; error: number; reviewed: number };
  findings_30d: number;
  usb_events_7d: number;
  usb_mode_default: DlpUsbMode;
  /** `dlp.upload` が無効なら null(docs/dlp-upload.md §9.2) */
  upload: DlpUploadOverview | null;
};

/** スキャン・USB ログに付く端末の要約。 */
export type DlpDeviceBrief = { id: number; serial: string; hostname: string; os: string };

/** `dlp_devices` の 1 行。`employee` は directory の brief(匿名化済みなら null)。 */
export type DlpDevice = {
  id: number;
  serial: string;
  hostname: string;
  os: string;
  mdm_device_id: number | null;
  employee_id: number | null;
  employee: EmployeeBrief | null;
  agent_version: string;
  capabilities: string[];
  usb_mode: DlpUsbMode | null;
  config_version: number;
  last_poll: string | null;
  last_report: string | null;
  /** エージェントが poll で送るアップロード観測の状態。capability `upload` の無い端末は null */
  upload_status: DlpUploadStatus | null;
  upload_status_at: string | null;
  created_at: string;
  updated_at: string;
};

/** 端末の詳細。直近スキャン 5 件・直近 USB 20 件・直近アップロード検知 20 件(`dlp.upload` 無効なら空)が付く。 */
export type DlpDeviceDetail = DlpDevice & {
  scans: DlpScan[];
  usb_events: DlpUsbEvent[];
  upload_events: DlpUploadEvent[];
};

export type DlpPolicyPattern = { name: string; regex: string };

/** 検査ポリシー(`dlp_scan_policies`)。`patterns[].regex` は RE2 で動く式に限る(検証はサーバー)。 */
export type DlpPolicy = {
  id: number;
  name: string;
  description: string;
  patterns: DlpPolicyPattern[];
  roots: string[];
  /** 小文字・ドット無し */
  extensions: string[];
  max_file_mb: number;
  max_files: number;
  time_budget_min: number;
  is_default: boolean;
  created_at: string;
  updated_at: string;
};

export type DlpPolicyInput = Omit<DlpPolicy, "id" | "created_at" | "updated_at">;

/** エージェントが返す走査の集計(`summary`)。古いエージェントは項目が欠けることがある。 */
export type DlpScanSummary = {
  files_seen?: number;
  files_scanned?: number;
  files_skipped?: number;
  bytes?: number;
  matches?: number;
  duration_ms?: number;
  truncated?: boolean;
  skipped_reasons?: Record<string, number>;
};

export type DlpScan = {
  id: number;
  device_id: number;
  device: DlpDeviceBrief | null;
  employee_id: number | null;
  employee: EmployeeBrief | null;
  policy_id: number | null;
  /** エージェントに渡した内容そのもの(一覧では省かれることがある) */
  policy_snapshot?: (Partial<DlpPolicyInput> & { name?: string }) | null;
  trigger: DlpScanTrigger;
  status: DlpScanStatus;
  requested_by: string;
  source_event_id: number | null;
  note: string;
  created_at: string;
  sent_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  summary: DlpScanSummary | null;
  error: string | null;
  reviewed_by: string;
  reviewed_at: string | null;
  review_note: string;
  findings_count: number;
};

/** 検出 1 件(ファイル × パターン)。本文(スニペット)は持たない。 */
export type DlpFinding = {
  id: number;
  scan_id: number;
  path: string;
  pattern: string;
  match_count: number;
  size_bytes: number;
  modified_at: string | null;
  created_at: string;
};

export type DlpUsbEvent = {
  id: number;
  device_id: number;
  device: DlpDeviceBrief | null;
  uid: string;
  at: string;
  kind: DlpUsbKind;
  drive: string;
  label: string;
  volume_serial: string;
  vendor: string;
  product: string;
  device_serial: string;
  username: string;
  created_at: string;
};

/** core settings の `dlp`。範囲は §7(usb_poll_sec 5〜300 / poll_sec 60〜3600 / report_sec 30〜600)。 */
export type DlpSettings = MailSettings & {
  offboard_auto_scan: boolean;
  usb_log: boolean;
  usb_poll_sec: number;
  usb_mode_default: DlpUsbMode;
  poll_sec: number;
  report_sec: number;
  // docs/dlp-upload.md §3
  upload_monitor: boolean;
  upload_block: boolean;
  /** 60〜3600 かつ 60 の倍数 */
  upload_window_sec: number;
  /** 64〜1048576 */
  upload_min_kib: number;
  /** 0〜100000(0 = 無効) */
  upload_unknown_mib: number;
  upload_disable_doh: boolean;
};

// ---- DLP アップロード検知(R6c-2)。docs/dlp-upload.md §9.2 ----

export type DlpUploadKind = "detected" | "unknown_bulk";
export type DlpUploadCategory = "storage" | "transfer" | "ai" | "other";
export type DlpUploadAttribution = "exact" | "ambiguous";
export type DlpUploadAction = "monitor" | "block" | "allow";
export type DlpUploadProcessDir = "program_files" | "windows" | "user_profile" | "other" | "unknown";

/** 窓(既定 5 分)ごとの推定送信 1 件。`employee` は取り込み時の端末の割当(送った本人とは限らない)。 */
export type DlpUploadEvent = {
  id: number;
  device: DlpDeviceBrief;
  employee: EmployeeBrief | null;
  kind: DlpUploadKind;
  window_start: string;
  window_end: string;
  process: string;
  process_dir: DlpUploadProcessDir;
  rule_domain: string | null;
  category: DlpUploadCategory | null;
  attribution: DlpUploadAttribution | null;
  bytes_sent: number;
  flows: number;
  protocols: ("tcp" | "udp")[];
  remote_ips: string[];
  lost: boolean;
  created_at: string;
};

/** 送信先の一覧の 1 行。builtin は削除できず domain も変えられない(enabled / action / note は変えられる)。 */
export type DlpUploadDomain = {
  id: number;
  domain: string;
  category: DlpUploadCategory;
  action: DlpUploadAction;
  enabled: boolean;
  builtin: boolean;
  note: string;
  created_at: string;
  updated_at: string;
};

export type DlpUploadDomainInput = Pick<DlpUploadDomain, "domain" | "category" | "action" | "enabled" | "note">;

export type DlpUploadState = "off" | "starting" | "observing" | "degraded" | "error" | "unsupported" | "invalid";
export type DlpBrowserPolicyState = "off" | "applied" | "external" | "conflict" | "absent" | "error" | "invalid";
export type DlpBrowser = "chrome" | "edge" | "firefox";

export type DlpUploadStatus = {
  state: DlpUploadState;
  error: string | null;
  applied_version: number | null;
  events_lost: number;
  app_dropped: number;
  overflow: number;
  last_event_at: string | null;
  block: Record<DlpBrowser, DlpBrowserPolicyState>;
  doh: Record<DlpBrowser, DlpBrowserPolicyState>;
};

export type DlpUploadOverview = {
  detected_7d: number;
  ambiguous_7d: number;
  unknown_bulk_7d: number;
  devices_observing: number;
  devices_error: number;
  /** 上位 5(exact と ambiguous の合計) */
  top_domains_7d: { rule_domain: string; bytes: number; devices: number }[];
};


function headers(token: string | undefined, init?: RequestInit): Record<string, string> {
  const out: Record<string, string> = { "Content-Type": "application/json" };
  if (token) out.Authorization = `Bearer ${token}`;
  for (const [k, v] of Object.entries((init?.headers ?? {}) as Record<string, string>)) {
    out[k] = v;
  }
  if (out["Content-Type"] === "") delete out["Content-Type"];
  return out;
}

export function createApi(apiBase: string, getToken: () => string | undefined) {
  async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const token = getToken();
    const res = await fetch(apiBase + path, {
      ...init,
      cache: path.startsWith("/api/dlp/mail/") ? "no-store" : init?.cache,
      headers: headers(token, init),
    });
    if (!res.ok) {
      if (res.status === 401) window.dispatchEvent(new Event("itmgmt-unauthorized"));
      const body = (await res.json().catch(() => null)) as
        | { error?: { code?: string; message?: string; reason?: string } & Record<string, unknown> }
        | null;
      throw new ApiError(
        res.status,
        body?.error?.code ?? "error",
        body?.error?.message ?? `HTTP ${res.status}`,
        body?.error?.reason,
        res.headers.has("Retry-After") ? Number(res.headers.get("Retry-After")) : undefined,
        body?.error && typeof body.error === "object" ? body.error : undefined,
      );
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  return {
    request,
    helpChat: async (body: unknown, signal: AbortSignal) => {
      const res = await fetch(apiBase + "/api/help/chat", { method: "POST", signal,
        headers: headers(getToken(), { headers: { "Content-Type": "application/json" } }), body: JSON.stringify(body) });
      if (!res.ok) {
      if (res.status === 401) window.dispatchEvent(new Event("itmgmt-unauthorized"));
        const data = await res.json().catch(() => null) as { error?: { code?: string; message?: string; reason?: string } } | null;
        throw new ApiError(res.status, data?.error?.code ?? "error", data?.error?.message ?? `HTTP ${res.status}`,
          data?.error?.reason, res.headers.has("Retry-After") ? Number(res.headers.get("Retry-After")) : undefined);
      }
      return res;
    },
    manualIndex: (signal?: AbortSignal) => request<ManualIndex>("/api/core/manual", { signal, cache: "no-store" }),
    manualDoc: (module: string, signal?: AbortSignal) => request<ManualDoc>(
      `/api/core/manual/${encodeURIComponent(module)}`, { signal, cache: "no-store" }),
    manualImage: async (path: string, signal?: AbortSignal) => {
      if (!/^\/api\/core\/manual\/(common|hr|mdm|idp|dlp|admin|reception)\/img\/[a-z0-9-]+\.png$/.test(path)) {
        throw new ApiError(404, "not_found", "画像が見つかりません");
      }
      const res = await fetch(apiBase + path, { signal, cache: "no-store", redirect: "error",
        headers: headers(getToken()) });
      if (!res.ok) throw new ApiError(res.status, "error", "画像を取得できませんでした");
      return res.blob();
    },
    me: () => request<Me>("/api/core/me"),
    apps: () => request<Page<AppTile>>("/api/core/apps"),
    audit: (params: URLSearchParams) =>
      request<Page<AuditRow>>(`/api/core/audit?${params.toString()}`),
    settings: () => request<Page<SettingRow>>("/api/core/settings"),
    license: () => request<LicenseInfo>("/api/core/license"),
    // 貼り替えは専用の口。検証してから保存するので、駄目なら 400 が返る
    saveLicense: (jwt: string) =>
      request<LicenseInfo>("/api/core/license", {
        method: "PUT",
        body: JSON.stringify({ jwt }),
      }),
    saveSetting: (key: string, data: Record<string, unknown>) =>
      request<SettingRow>(`/api/core/settings/${encodeURIComponent(key)}`, {
        method: "PUT",
        body: JSON.stringify({ data }),
      }),

    // ---- 人事(R2)---------------------------------------------------------
    employees: (params: URLSearchParams, signal?: AbortSignal) =>
      request<Page<Employee>>(`/api/hr/employees?${params.toString()}`,
        signal ? { signal } : undefined),
    employee: (id: number) => request<EmployeeDetail>(`/api/hr/employees/${id}`),
    createEmployee: (body: EmployeeInput) =>
      request<EmployeeDetail>("/api/hr/employees", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    /** 同じ氏名(空白を無視)か同じメールの従業員。退職済み・匿名化済みは出さない */
    similarEmployees: (q: { family_name?: string; given_name?: string; email?: string },
      signal?: AbortSignal) => {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(q)) if (v) params.set(k, v);
      return request<SimilarEmployee[]>(`/api/hr/employees/similar?${params.toString()}`,
        signal ? { signal } : undefined);
    },
    /** 従業員の登録と入社申請を1回で(どちらかが失敗したら両方残らない) */
    createOnboarding: (body: { employee: EmployeeInput; request: OnboardingRequestInput }) =>
      request<{ employee: EmployeeDetail; request: WfRequestDetail }>("/api/hr/onboarding", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    updateEmployee: (id: number, body: EmployeeInput) =>
      request<EmployeeDetail>(`/api/hr/employees/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    renumberPreview: (id: number, employmentType: string) =>
      request<RenumberPreview>(
        `/api/hr/employees/${id}/renumber-preview?employment_type=${encodeURIComponent(employmentType)}`,
      ),
    addConcurrent: (id: number, body: { unit_id: number; label?: string; sort?: number }) =>
      request<EmployeeDetail>(`/api/hr/employees/${id}/concurrent`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    deleteConcurrent: (id: number, concurrentId: number) =>
      request<EmployeeDetail>(`/api/hr/employees/${id}/concurrent/${concurrentId}`, {
        method: "DELETE",
      }),
    orgUnits: (activeOnly = false) =>
      request<Page<OrgUnit>>(`/api/hr/org/units${activeOnly ? "?active=true" : ""}`),
    createOrgUnit: (body: OrgUnitInput) =>
      request<OrgUnit>("/api/hr/org/units", { method: "POST", body: JSON.stringify(body) }),
    updateOrgUnit: (id: number, body: OrgUnitInput) =>
      request<OrgUnit>(`/api/hr/org/units/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    positions: (activeOnly = false) =>
      request<Page<Position>>(`/api/hr/org/positions${activeOnly ? "?active=true" : ""}`),
    createPosition: (body: { name: string; rank?: number }) =>
      request<Position>("/api/hr/org/positions", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    updatePosition: (id: number, body: { name?: string; rank?: number; active?: boolean }) =>
      request<Position>(`/api/hr/org/positions/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    customFields: (activeOnly = false) =>
      request<Page<CustomField>>(`/api/hr/custom-fields${activeOnly ? "?active=true" : ""}`),
    createCustomField: (body: CustomFieldInput) =>
      request<CustomField>("/api/hr/custom-fields", { method: "POST", body: JSON.stringify(body) }),
    updateCustomField: (id: number, body: CustomFieldInput) =>
      request<CustomField>(`/api/hr/custom-fields/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    deleteCustomField: (id: number) =>
      request<void>(`/api/hr/custom-fields/${id}`, { method: "DELETE" }),
    orgChart: (params: URLSearchParams) =>
      request<ChartOut>(`/api/hr/org/chart?${params.toString()}`),
    orgReporting: (params: URLSearchParams) =>
      request<ReportingOut>(`/api/hr/org/reporting?${params.toString()}`),

    // ---- 貸与品台帳・備品マスタ ---------------------------------------------
    loans: (params: URLSearchParams) => request<Page<Loan>>(`/api/hr/loans?${params.toString()}`),
    employeeLoans: (employeeId: number, params?: URLSearchParams) =>
      request<Page<Loan>>(
        `/api/hr/employees/${employeeId}/loans${params ? `?${params.toString()}` : ""}`,
      ),
    equipment: (params: URLSearchParams) =>
      request<Page<Equipment>>(`/api/hr/equipment?${params.toString()}`),
    equipmentItem: (id: number) => request<Equipment>(`/api/hr/equipment/${id}`),
    equipmentCategories: () =>
      request<{ categories: string[]; statuses: string[]; ownerships: string[] }>(
        "/api/hr/equipment/categories",
      ),
    createEquipment: (body: EquipmentInput) =>
      request<Equipment>("/api/hr/equipment", { method: "POST", body: JSON.stringify(body) }),
    updateEquipment: (id: number, body: EquipmentPatch) =>
      request<Equipment>(`/api/hr/equipment/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    /** 申請を通さない即時貸出(備品ページからの直接操作)。 */
    lendEquipment: (id: number, body: { employee_id: number; note?: string; lent_at?: string }) =>
      request<Equipment>(`/api/hr/equipment/${id}/lend`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    /** MDM の端末を手で紐づける(null で外して自動に戻す)。別の個体に付いた端末は 409。 */
    setEquipmentDevice: (id: number, device_id: number | null) =>
      request<Equipment>(`/api/hr/equipment/${id}/device`, {
        method: "PUT",
        body: JSON.stringify({ device_id }),
      }),
    returnEquipment: (id: number, note = "") =>
      request<Equipment>(`/api/hr/equipment/${id}/return`, {
        method: "POST",
        body: JSON.stringify({ note }),
      }),

    // ---- 月次人員棚卸 --------------------------------------------------------
    monthly: (params: URLSearchParams) =>
      request<MonthlyOut>(`/api/hr/monthly?${params.toString()}`),

    // ---- カタログ・割当(読み取りは core、書き込みは人事)--------------------
    catalog: (params?: URLSearchParams) =>
      request<Page<CatalogItem>>(`/api/core/catalog${params ? `?${params.toString()}` : ""}`),
    partners: (params?: URLSearchParams) =>
      request<Page<Partner>>(`/api/core/partners${params ? `?${params.toString()}` : ""}`),
    assignments: (params: URLSearchParams) =>
      request<Page<Assignment>>(`/api/core/assignments?${params.toString()}`),
    createCatalogItem: (body: CatalogItemInput) =>
      request<CatalogItem>("/api/hr/catalog/items", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    updateCatalogItem: (id: number, body: CatalogItemInput) =>
      request<CatalogItem>(`/api/hr/catalog/items/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    deleteCatalogItem: (id: number) =>
      request<{ deleted: string }>(`/api/hr/catalog/items/${id}`, { method: "DELETE" }),
    createPartner: (body: PartnerInput) =>
      request<Partner>("/api/hr/partners", { method: "POST", body: JSON.stringify(body) }),
    updatePartner: (id: number, body: PartnerInput) =>
      request<Partner>(`/api/hr/partners/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    deletePartner: (id: number) =>
      request<{ deleted: string }>(`/api/hr/partners/${id}`, { method: "DELETE" }),

    // ---- WF テンプレート -----------------------------------------------------
    wfTemplates: (kind = "") =>
      request<Page<WfTemplate>>(
        `/api/hr/wf/templates?limit=200${kind ? `&kind=${encodeURIComponent(kind)}` : ""}`,
      ),
    wfTemplate: (id: number) => request<WfTemplate>(`/api/hr/wf/templates/${id}`),
    wfDefaults: () => request<WfDefaults>("/api/hr/wf/defaults"),
    createTemplate: (body: { name: string; kind: string; note?: string }) =>
      request<WfTemplate>("/api/hr/wf/templates", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    updateTemplate: (id: number, body: { name?: string; kind?: string; note?: string }) =>
      request<WfTemplate>(`/api/hr/wf/templates/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    deleteTemplate: (id: number) =>
      request<Record<string, unknown>>(`/api/hr/wf/templates/${id}`, { method: "DELETE" }),
    duplicateTemplate: (id: number, name = "") =>
      request<WfTemplate>(`/api/hr/wf/templates/${id}/duplicate`, {
        method: "POST",
        body: JSON.stringify({ name }),
      }),
    setDefaultTemplate: (id: number) =>
      request<{ default_templates: Record<string, number> }>(
        `/api/hr/wf/templates/${id}/default`,
        { method: "POST" },
      ),
    createSection: (templateId: number, body: { name: string; sort?: number }) =>
      request<Record<string, unknown>>(`/api/hr/wf/templates/${templateId}/sections`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    updateSection: (templateId: number, id: number, body: { name?: string; sort?: number }) =>
      request<Record<string, unknown>>(`/api/hr/wf/templates/${templateId}/sections/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    deleteSection: (templateId: number, id: number) =>
      request<Record<string, unknown>>(`/api/hr/wf/templates/${templateId}/sections/${id}`, {
        method: "DELETE",
      }),
    createStep: (templateId: number, body: Partial<TemplateStep> & { name: string }) =>
      request<Record<string, unknown>>(`/api/hr/wf/templates/${templateId}/steps`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    updateStep: (templateId: number, id: number, body: Partial<TemplateStep>) =>
      request<Record<string, unknown>>(`/api/hr/wf/templates/${templateId}/steps/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    deleteStep: (templateId: number, id: number) =>
      request<Record<string, unknown>>(`/api/hr/wf/templates/${templateId}/steps/${id}`, {
        method: "DELETE",
      }),
    putTemplateItems: (
      templateId: number,
      items: { item_id: number; section_id?: number | null; required?: boolean; sort?: number }[],
    ) =>
      request<WfTemplate>(`/api/hr/wf/templates/${templateId}/items`, {
        method: "PUT",
        body: JSON.stringify({ items }),
      }),
    putTemplateFields: (
      templateId: number,
      fields: {
        name: string;
        ftype?: string;
        options?: string[];
        section_id?: number | null;
        required?: boolean;
        source?: string;
      }[],
    ) =>
      request<WfTemplate>(`/api/hr/wf/templates/${templateId}/fields`, {
        method: "PUT",
        body: JSON.stringify({ fields }),
      }),

    // ---- 申請・承認 ----------------------------------------------------------
    wfRequests: (params: URLSearchParams) =>
      request<Page<WfRequest>>(`/api/hr/requests?${params.toString()}`),
    wfRequest: (id: number) => request<WfRequestDetail>(`/api/hr/requests/${id}`),
    wfApprovers: (kind: string, id: number) =>
      request<WfApproverPreview>(`/api/hr/wf/approvers?kind=${encodeURIComponent(kind)}&id=${id}`),
    createRequest: (body: WfRequestInput) =>
      request<WfRequestDetail>("/api/hr/requests", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    approveRequest: (id: number, reason = "") =>
      request<{ status: string; approvals: string; approved_by: string[]; id: number }>(
        `/api/hr/requests/${id}/approve`,
        { method: "POST", body: JSON.stringify({ reason }) },
      ),
    rejectRequest: (id: number, reason: string) =>
      request<{ status: string; id: number }>(`/api/hr/requests/${id}/reject`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),
    /** 承認待ちの申請を取り下げる(hr:operator)。deleteEmployee は入社申請だけ */
    withdrawRequest: (id: number, reason: string, deleteEmployee = false) =>
      request<{ id: number; status: "withdrawn"; employee_deleted: boolean }>(
        `/api/hr/requests/${id}/withdraw`,
        { method: "POST", body: JSON.stringify({ reason, delete_employee: deleteEmployee }) },
      ),
    /** 却下・取り下げた申請を消す(hr:admin)。承認待ち・承認済みは 409。 */
    deleteRequest: (id: number) =>
      request<{ deleted: number }>(`/api/hr/requests/${id}`, { method: "DELETE" }),
    employeeWfStatus: (employeeId: number) =>
      request<EmployeeWfStatus>(`/api/hr/employees/${employeeId}/wf-status`),

    // ---- タスク -------------------------------------------------------------
    wfTasks: (params: URLSearchParams) =>
      request<Page<WfTask>>(`/api/hr/tasks?${params.toString()}`),
    completeTask: (id: number, result: string, equipmentId?: number | null) =>
      request<WfTask>(`/api/hr/tasks/${id}/complete`, {
        method: "POST",
        body: JSON.stringify({ result, ...(equipmentId ? { equipment_id: equipmentId } : {}) }),
      }),

    /**
     * 失敗した自動タスクをもう一度依頼する(hr:approver 以上)。
     * 実行の依頼がまだ残っているときは 409。
     */
    rerunTask: (id: number) => request<WfTask>(`/api/hr/tasks/${id}/rerun`, { method: "POST" }),

    /** まとめて完了 / 対象外にする。自動実行待ちが混ざっていれば 409(1 件も変えない)。 */
    bulkTasks: (ids: number[], action: "done" | "skip", note = "") =>
      request<{ action: string; changed: number[]; ignored: number[]; count: number }>(
        "/api/hr/tasks/bulk",
        { method: "POST", body: JSON.stringify({ ids, action, note }) },
      ),

    // ---- 社外依頼 -----------------------------------------------------------
    externalRequests: (params: URLSearchParams) =>
      request<Page<WfExternalRequest>>(`/api/hr/external-requests?${params.toString()}`),
    externalRequest: (id: number) =>
      request<WfExternalRequest>(`/api/hr/external-requests/${id}`),
    updateExternalRequest: (id: number, body: { body: string }) =>
      request<WfExternalRequest>(`/api/hr/external-requests/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    sendExternalRequest: (id: number) =>
      request<{ id: number; status: string; sent: boolean; to?: string; message?: string }>(
        `/api/hr/external-requests/${id}/send`,
        { method: "POST" },
      ),
    setExternalStatus: (id: number, status: string) =>
      request<WfExternalRequest>(`/api/hr/external-requests/${id}/status`, {
        method: "POST",
        body: JSON.stringify({ status }),
      }),
    mailLog: (params: URLSearchParams) =>
      request<Page<MailLogRow>>(`/api/hr/mail-log?${params.toString()}`),

    /** 送った後は 409。言語だけ選んで雛形から作り直す。 */
    rerenderExternalRequest: (id: number, lang: string) =>
      request<WfExternalRequest>(`/api/hr/external-requests/${id}/rerender`, {
        method: "POST",
        body: JSON.stringify({ lang }),
      }),

    // ---- 社外依頼メールの雛形(設定)-----------------------------------------
    externalMail: () => request<ExternalMailSettings>("/api/hr/settings/external-mail"),
    saveExternalMail: (external_mail: ExternalMailTexts) =>
      request<ExternalMailSettings>("/api/hr/settings/external-mail", {
        method: "PUT",
        body: JSON.stringify({ external_mail }),
      }),
    /** 未保存の文面をそのまま見本値で描く(空なら保存済みを使う)。 */
    previewExternalMail: (body: { kind: string; lang: string; subject?: string; body?: string }) =>
      request<{ subject: string; body: string }>("/api/hr/settings/external-mail/preview", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    resetExternalMail: (kind: string, lang: string) =>
      request<ExternalMailSettings>(`/api/hr/settings/external-mail/${kind}/${lang}`, {
        method: "DELETE",
      }),

    // ---- 動的グループ(HR3)---------------------------------------------------
    groupFields: () => request<GroupFields>("/api/hr/groups/fields"),
    groups: (params?: URLSearchParams) =>
      request<Page<DynGroup>>(`/api/hr/groups${params ? `?${params.toString()}` : ""}`),
    group: (id: number) => request<DynGroup>(`/api/hr/groups/${id}`),
    createGroup: (body: DynGroupInput) =>
      request<DynGroup>("/api/hr/groups", { method: "POST", body: JSON.stringify(body) }),
    updateGroup: (id: number, body: DynGroupInput) =>
      request<DynGroup>(`/api/hr/groups/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    deleteGroup: (id: number) =>
      request<{ ok: boolean }>(`/api/hr/groups/${id}`, { method: "DELETE" }),
    /** 保存せずに人数を数える。 */
    previewGroup: (rule: GroupRuleNode) =>
      request<GroupPreview>("/api/hr/groups/preview", {
        method: "POST",
        body: JSON.stringify({ rule }),
      }),
    groupMembers: (id: number, params: URLSearchParams) =>
      request<Page<GroupMember>>(`/api/hr/groups/${id}/members?${params.toString()}`),
    groupEvents: (id: number) =>
      request<{ items: GroupEvent[] }>(`/api/hr/groups/${id}/events`),
    reevaluateGroup: (id: number) =>
      request<{ joined: number[]; left: number[]; member_count: number; skipped: string }>(
        `/api/hr/groups/${id}/reevaluate`,
        { method: "POST" },
      ),
    /** 今のメンバー全員ぶんの付与タスクを起票する(初期同期)。 */
    syncGroup: (id: number) =>
      request<GroupSyncResult>(`/api/hr/groups/${id}/sync`, {
        method: "POST",
      }),

    // ---- 権限セット -----------------------------------------------------------
    permissionSets: (groupId: number) =>
      request<{ items: PermissionSet[] }>(`/api/hr/groups/${groupId}/permission-sets`),
    createPermissionSet: (groupId: number, body: PermissionSetInput) =>
      request<PermissionSet>(`/api/hr/groups/${groupId}/permission-sets`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    updatePermissionSet: (id: number, body: PermissionSetInput) =>
      request<PermissionSet>(`/api/hr/permission-sets/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    deletePermissionSet: (id: number) =>
      request<{ ok: boolean }>(`/api/hr/permission-sets/${id}`, {
        method: "DELETE",
      }),
    /** この権限セットが配ったものを全部剥がす(台帳に行がある人ぶんだけ起票する) */
    revokeAllPermissionSet: (id: number) =>
      request<GroupSyncResult>(`/api/hr/permission-sets/${id}/revoke-all`, {
        method: "POST",
      }),

    /** GWS の組織部門(OU)を組織マスタに合わせる作業を 1 件起票する。走っていれば 409。 */
    syncOrgUnitsToGws: () =>
      request<{ task_id: number; mode: string; units: number }>("/api/hr/org-units/gws-sync", {
        method: "POST",
      }),

    // ---- レポート -----------------------------------------------------------
    reportSummary: (month = "") =>
      request<SlaSummary>(`/api/hr/report/summary${month ? `?month=${month}` : ""}`),

    // ---- 完了報告(ログイン不要。トークンが鍵)-------------------------------
    publicReport: (token: string) =>
      request<PublicReport>(`/api/hr/public/report/${encodeURIComponent(token)}`),
    submitPublicReport: (token: string, note: string) =>
      request<{ id: number; status: string; reported: boolean }>(
        `/api/hr/public/report/${encodeURIComponent(token)}`,
        { method: "POST", body: JSON.stringify({ note }) },
      ),

    // ---- お知らせ(suite 共通)-------------------------------------------
    notifications: (params?: URLSearchParams) =>
      request<NotificationPage>(`/api/core/notifications${params ? `?${params.toString()}` : ""}`),
    readNotification: (id: number) =>
      request<NotificationRow>(`/api/core/notifications/${id}/read`, { method: "POST" }),
    /** 既読のお知らせをまとめて消す(未読は残る) */
    deleteReadNotifications: () =>
      request<{ deleted: number }>("/api/core/notifications/read", { method: "DELETE" }),
    /** 既読のお知らせを 1 件消す(未読は 409) */
    deleteNotification: (id: number) =>
      request<{ deleted: number }>(`/api/core/notifications/${id}`, { method: "DELETE" }),

    // ---- HR取込・匿名化・保有期間 ---------------------------------------
    /** CSV の判定(何も書かない)。反映に使う引換券が返る。 */
    judgeHrImport: async (file: File) => {
      const form = new FormData();
      form.append("file", file);
      form.append("dry_run", "true");
      // multipart は境界を fetch に決めさせる(Content-Type を自分で付けない)
      return await request<ImportResult>("/api/hr/import", {
        method: "POST",
        body: form,
        headers: { "Content-Type": "" },
      });
    },
    applyHrImport: (token: string) =>
      request<ImportResult>("/api/hr/import/apply", {
        method: "POST",
        body: JSON.stringify({ token }),
      }),
    anonymizeCandidates: () =>
      request<AnonymizeCandidates>("/api/hr/anonymize/candidates"),
    anonymizeEmployee: (id: number) =>
      request<{ id: number; emp_code: string; name: string; history_rows: number }>(
        `/api/hr/employees/${id}/anonymize`,
        { method: "POST" },
      ),
    deleteEmployee: (id: number) =>
      request<{ deleted: number; emp_code: string }>(`/api/hr/employees/${id}`, {
        method: "DELETE",
      }),
    retentionStatus: () => request<{ days: number; max_days: number }>("/api/hr/jobs/retention"),
    runRetention: () =>
      request<RetentionRun>("/api/hr/jobs/retention/run", { method: "POST" }),

    directoryEmployees: (params: URLSearchParams) =>
      request<Page<EmployeeBrief>>(`/api/core/directory/employees?${params.toString()}`),

    // ---- デバイス管理(R4)-------------------------------------------------
    devices: (params: URLSearchParams) =>
      request<Page<Device>>(`/api/mdm/devices?${params.toString()}`),
    device: (id: number) => request<DeviceDetail>(`/api/mdm/devices/${id}`),
    updateDevice: (id: number, body: { employee_id?: number | null; unassign?: boolean }) =>
      request<DeviceDetail>(`/api/mdm/devices/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    registryPolicies: () => request<RegistryPolicies>("/api/mdm/registry-policies"),
    registryTemplates: () => request<RegistryTemplate[]>("/api/mdm/registry-policies/templates"),
    registryGroups: () => request<RegistryGroup[]>("/api/mdm/registry-policies/groups"),
    /** 保存前の確認。対象ごとに今当たる台数と、指定した端末に当たる理由。 */
    registryPreview: (targets: RegistryTarget[], deviceId?: number | null) =>
      request<RegistryPreview>("/api/mdm/registry-policies/preview", {
        method: "POST",
        body: JSON.stringify({ targets, device_id: deviceId ?? null }),
      }),
    deviceRegistryPolicies: (deviceId: number) =>
      request<DeviceRegistryPolicy[]>(`/api/mdm/devices/${deviceId}/registry-policies`),
    agentPackage: () => request<AgentPackage>("/api/mdm/agent-package"),
    enrollCodes: () => request<EnrollCode[]>("/api/mdm/enroll-codes"),
    issueEnrollCode: (data: { memo: string; ttl_hours: number; max_uses: number; device_id?: number }) =>
      request<EnrollCodeIssued>("/api/mdm/enroll-codes", { method: "POST", body: JSON.stringify(data) }),
    revokeEnrollCode: (id: number) =>
      request<EnrollCode>(`/api/mdm/enroll-codes/${id}/revoke`, { method: "POST" }),
    deviceAgentTokens: (deviceId: number) =>
      request<DeviceAgentToken[]>(`/api/mdm/devices/${deviceId}/agent-tokens`),
    unboundAgentTokens: () => request<DeviceAgentToken[]>("/api/mdm/agent-tokens/unbound"),
    revokeAgentToken: (id: number) =>
      request<DeviceAgentToken>(`/api/mdm/agent-tokens/${id}/revoke`, { method: "POST" }),
    saveRegistryPolicies: (policies: RegistryPolicy[]) =>
      request<RegistryPolicies>("/api/mdm/registry-policies", {
        method: "PUT",
        body: JSON.stringify({ policies }),
      }),
    /** 未登録でも 404 にならない(enrollment: null が返る)。 */
    deviceCommands: (id: number, params?: URLSearchParams) =>
      request<DeviceCommands>(
        `/api/mdm/devices/${id}/commands` + (params ? `?${params.toString()}` : ""),
      ),
    /**
     * コマンドを1件積む。**202 は「NanoMDM に渡した」まで。**
     * 届いたかどうかは戻りの `status` を見る(`push_failed` なら端末には行っていない)。
     */
    enqueueDeviceCommand: (
      id: number,
      body: { request_type: string; params?: Record<string, unknown>; confirm?: string },
    ) =>
      request<MdmCommand>(`/api/mdm/devices/${id}/commands`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    /** 積んだままのコマンドを端末に取りに来させる(再 push)。 */
    pushDevice: (id: number) =>
      request<{ udid: string; result: Record<string, unknown> }>(
        `/api/mdm/devices/${id}/push`,
        { method: "POST" },
      ),
    mdmEnrollments: (params?: URLSearchParams) =>
      request<Page<MdmEnrollment>>(
        "/api/mdm/enrollments" + (params ? `?${params.toString()}` : ""),
      ),
    /** APNs の push 証明書(PEM 2つ)。戻りの topic を設定 `mdm.topic` に控える。 */
    uploadPushCert: (cert: string, key: string) =>
      request<{ topic: string; result: Record<string, unknown> }>("/api/mdm/pushcert", {
        method: "PUT",
        body: JSON.stringify({ cert, key }),
      }),
    /** 無視・失敗した Webhook の件数(直近 24 時間と累計)。値は入っていない。 */
    mdmWebhookStatus: () => request<MdmWebhookStatus>("/api/mdm/webhook/status"),
    mdmSetting: (key: string) =>
      request<MdmSetting>(`/api/mdm/settings/${encodeURIComponent(key)}`),
    saveMdmSetting: (key: string, data: Record<string, unknown>) =>
      request<MdmSetting>(`/api/mdm/settings/${encodeURIComponent(key)}`, {
        method: "PUT",
        body: JSON.stringify({ data }),
      }),

    // ---- Android MDM(MOB2)-------------------------------------------------
    /** 設定(mdm:admin だけ)。SA の鍵は伏字で返る。 */
    androidConfig: () => request<AndroidConfig>("/api/mdm/android/config"),
    saveAndroidConfig: (data: Record<string, unknown>) =>
      request<AndroidConfig>("/api/mdm/android/config", {
        method: "PUT",
        body: JSON.stringify({ data }),
      }),
    /** 紐付け1段目。返った `url` を管理者がブラウザで開く(**控えない**)。 */
    androidSignupUrl: () =>
      request<AndroidSignupUrl>("/api/mdm/android/signup-url", { method: "POST" }),
    /** 紐付け2段目。同意画面の戻りの `enterpriseToken` と引き換える。 */
    bindAndroidEnterprise: (body: {
      enterprise_token: string;
      signup_url_name: string;
      display_name?: string;
    }) =>
      request<AndroidBindResult>("/api/mdm/android/enterprise", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    /**
     * 紐付けを**外す**(設定の `enterprise` を消す。中 7)。
     *
     * 空文字の保存は「変更なし」なので、消す道はここだけ。Google 側の企業と
     * 端末の対応表は残る(同じ企業に戻せば続きから動く)。
     */
    clearAndroidEnterprise: () =>
      request<AndroidConfig>("/api/mdm/android/enterprise", { method: "DELETE" }),
    /** 接続テスト(`enterprises.get`)。**失敗も 200 で ○× が返る。** */
    testAndroid: () =>
      request<AndroidTestResult>("/api/mdm/android/test", { method: "POST" }),
    androidPolicies: () => request<AndroidPolicies>("/api/mdm/android/policies"),
    saveAndroidPolicy: (
      policyId: string,
      body: { name?: string; template?: string; body: Record<string, unknown> },
    ) =>
      request<AndroidPolicy>(
        `/api/mdm/android/policies/${encodeURIComponent(policyId)}`,
        { method: "PUT", body: JSON.stringify(body) },
      ),
    androidTokens: (params?: URLSearchParams) =>
      request<AndroidTokens>(
        "/api/mdm/android/enrollment-tokens" + (params ? `?${params.toString()}` : ""),
      ),
    /** 発行。**戻りの `value` / `qr_code` はこの1回だけ**(保存していない)。 */
    createAndroidToken: (body: {
      policy_id?: string;
      duration?: number;
      one_time_only?: boolean;
      allow_personal_usage?: string;
      employee_email?: string;
    }) =>
      request<AndroidToken>("/api/mdm/android/enrollment-tokens", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    revokeAndroidToken: (id: number) =>
      request<AndroidToken>(`/api/mdm/android/enrollment-tokens/${id}`, {
        method: "DELETE",
      }),
    /** いま同期する。**失敗も 200**(`skipped` に理由が入る)。 */
    syncAndroid: () =>
      request<AndroidSyncResult>("/api/mdm/android/sync", { method: "POST" }),
    /** 未登録でも 404 にならない(`device: null` が返る)。 */
    androidDeviceCommands: (id: number, params?: URLSearchParams) =>
      request<AndroidDeviceCommands>(
        `/api/mdm/android/devices/${id}/commands` + (params ? `?${params.toString()}` : ""),
      ),
    /**
     * Android のコマンドを1件。**202 は「AMAPI が受けた」まで**で、効いたかは
     * 次の同期で `status` が `acknowledged` に変わるのを見る。
     *
     * `resend` は届いたか分からない同じ破壊操作が残っているときだけ `"resend"`
     * (無いと 409。二重に効く可能性の承知)。
     */
    androidCommand: (
      id: number,
      body: {
        type: string;
        params?: Record<string, unknown>;
        confirm?: string;
        resend?: string;
      },
    ) =>
      request<MdmCommand>(`/api/mdm/android/devices/${id}/commands`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    /**
     * 初期化(端末を初期化して企業から外す)。`confirm` に製造番号が要る。
     * `resend` は届いたか分からない初期化が残っているときだけ `"resend"`。
     */
    wipeAndroidDevice: (
      id: number,
      body: { confirm: string; wipe_data_flags?: string[]; resend?: string },
    ) =>
      request<MdmCommand>(`/api/mdm/android/devices/${id}`, {
        method: "DELETE",
        body: JSON.stringify(body),
      }),

    // ---- ID管理(R5)-------------------------------------------------------
    /** 最新の突合。1度も走っていなければ 204 なので undefined が返る。 */
    reconciliation: () =>
      request<ReconcileRunDetail | undefined>("/api/idp/reconciliation"),
    runReconciliation: () =>
      request<ReconcileRunDetail>("/api/idp/reconciliation/run", { method: "POST" }),
    reconcileRuns: (params: URLSearchParams) =>
      request<Page<ReconcileRun>>(`/api/idp/reconciliation/runs?${params.toString()}`),
    reconcileRun: (id: number) =>
      request<ReconcileRunDetail>(`/api/idp/reconciliation/runs/${id}`),
    idpConnectors: () => request<Page<IdpConnector>>("/api/idp/connectors"),
    /** 従業員の SaaS アカウントの初期パスワードを新しい値に置き換える(idp:operator。いまは gws だけ) */
    resetInitialPassword: (connector: string, employee_id: number) =>
      request<ResetInitialPasswordResult>("/api/idp/accounts/reset-initial-password", {
        method: "POST",
        body: JSON.stringify({ connector, employee_id }),
      }),
    testIdpConnector: (key: string) =>
      request<ConnectorTestResult>(
        `/api/idp/connectors/${encodeURIComponent(key)}/test`,
        { method: "POST" },
      ),
    idpSetting: (key: string) =>
      request<IdpSetting>(`/api/idp/settings/${encodeURIComponent(key)}`),
    saveIdpSetting: (key: string, data: Record<string, unknown>) =>
      request<IdpSetting>(`/api/idp/settings/${encodeURIComponent(key)}`, {
        method: "PUT",
        body: JSON.stringify({ data }),
      }),
    runIdpFulfill: () =>
      request<FulfillResult>("/api/idp/fulfill/run", { method: "POST" }),

    // ---- サービスプロバイダー(R7a)---------------------------------------
    providers: (params: URLSearchParams) =>
      request<Page<ServiceProvider>>(`/api/idp/providers?${params.toString()}`),
    provider: (id: number) => request<ServiceProviderDetail>(`/api/idp/providers/${id}`),
    createProvider: (kind: string, form: ProviderForm) =>
      request<ServiceProviderDetail>("/api/idp/providers", {
        method: "POST",
        body: JSON.stringify({ kind, form }),
      }),
    updateProvider: (id: number, form: ProviderForm) =>
      request<ServiceProviderDetail>(`/api/idp/providers/${id}`, {
        method: "PUT",
        body: JSON.stringify({ form }),
      }),
    deleteProvider: (id: number) =>
      request<{ name: string; warning: string }>(`/api/idp/providers/${id}`, {
        method: "DELETE",
      }),
    /** SP のメタデータ XML を読んでフォームの値にする(保存はしない)。 */
    importSamlMetadata: async (file: File) => {
      const form = new FormData();
      form.append("file", file);
      // multipart は境界を fetch に決めさせる(Content-Type を自分で付けない)
      return await request<{ form: ProviderForm }>("/api/idp/providers/import-saml-metadata", {
        method: "POST",
        body: form,
        headers: { "Content-Type": "" },
      });
    },
    /** IdP 情報。`id` を渡すとその SP のダイレクトログイン URL も入る。 */
    idpInfo: (id?: number) =>
      request<IdpInfo>(`/api/idp/providers/idp-info${id ? `?id=${id}` : ""}`),
    rotateOidcSecret: (id: number) =>
      request<{ secret: string }>(`/api/idp/providers/${id}/oidc-secret/rotate`, {
        method: "POST",
      }),
    /** ポータルの「サービス」節。ログインしていれば誰でも読める。 */
    portalServices: () => request<Page<PortalTile>>("/api/idp/portal"),

    // ---- ユーザー管理(R7b。正は Keycloak)--------------------------------
    idpUsers: (params: URLSearchParams) =>
      request<Page<IdpUser>>(`/api/idp/users?${params.toString()}`),
    idpUser: (id: string) => request<IdpUserDetail>(`/api/idp/users/${encodeURIComponent(id)}`),
    /** 作る。**戻りの `password` はこの1回だけ**(メールで送れたときは空)。 */
    createIdpUser: (form: IdpUserForm) =>
      request<IdpUserCreated>("/api/idp/users", {
        method: "POST",
        body: JSON.stringify(form),
      }),
    updateIdpUser: (id: string, form: IdpUserForm) =>
      request<IdpUser & { roles: string[] }>(`/api/idp/users/${encodeURIComponent(id)}`, {
        method: "PUT",
        body: JSON.stringify(form),
      }),
    deleteIdpUser: (id: string) =>
      request<{ id: string; username: string }>(`/api/idp/users/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    resetIdpPassword: (id: string, body: { generate: boolean; password?: string; temporary: boolean }) =>
      request<IdpPasswordResult>(
        `/api/idp/users/${encodeURIComponent(id)}/reset-password`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    logoutIdpUser: (id: string) =>
      request<{ username: string; sessions: number }>(
        `/api/idp/users/${encodeURIComponent(id)}/logout`,
        { method: "POST" },
      ),
    resetIdpOtp: (id: string) =>
      request<{ username: string; removed: number }>(
        `/api/idp/users/${encodeURIComponent(id)}/reset-otp`,
        { method: "POST" },
      ),
    unlockIdpUser: (id: string) =>
      request<{ username: string }>(`/api/idp/users/${encodeURIComponent(id)}/unlock`, {
        method: "POST",
      }),
    /** CSV の一括登録・一括更新。`dryRun` は検査だけ(何も書かない)。 */
    importIdpUsers: async (file: File, mode: string, dryRun: boolean) => {
      const form = new FormData();
      form.append("file", file);
      form.append("mode", mode);
      form.append("dry_run", dryRun ? "true" : "false");
      // multipart は境界を fetch に決めさせる(Content-Type を自分で付けない)
      return await request<BulkResult>("/api/idp/users/import", {
        method: "POST",
        body: form,
        headers: { "Content-Type": "" },
      });
    },
    idpBulkJobs: (params: URLSearchParams) =>
      request<Page<BulkJob>>(`/api/idp/users/import/jobs?${params.toString()}`),
    /** 取込 1 件の詳細(`status` が `running` の間は画面がポーリングする)。 */
    idpBulkJob: (id: number) =>
      request<BulkJob>(`/api/idp/users/import/jobs/${encodeURIComponent(id)}`),

    // ---- ログ(R7c)-------------------------------------------------------
    idpLogStatus: () => request<LogStatus>("/api/idp/logs/status"),
    /** 手動で 1 回取り込む(idp:admin。監査に残る)。 */
    idpLogPull: () => request<LogPullResult>("/api/idp/logs/pull", { method: "POST" }),
    idpLogSettings: () => request<{ data: LogSettings }>("/api/idp/logs/settings"),
    saveIdpLogSettings: (data: Partial<LogSettings>) =>
      request<{ data: LogSettings; changed: string[] }>("/api/idp/logs/settings", {
        method: "PUT",
        body: JSON.stringify({ data }),
      }),
    idpLogAccess: (params: URLSearchParams) =>
      request<Page<LogEvent>>(`/api/idp/logs/access?${params.toString()}`),
    idpLogUserOps: (params: URLSearchParams) =>
      request<Page<LogEvent>>(`/api/idp/logs/user-ops?${params.toString()}`),
    idpLogAdminOps: (params: URLSearchParams) =>
      request<Page<LogAdminOp>>(`/api/idp/logs/admin-ops?${params.toString()}`),
    idpLogSync: (params: URLSearchParams) =>
      request<Page<LogSync>>(`/api/idp/logs/sync?${params.toString()}`),
    idpLogBulk: (params: URLSearchParams) =>
      request<Page<BulkJob>>(`/api/idp/logs/bulk?${params.toString()}`),
    idpLogBulkJob: (id: number) =>
      request<BulkJob>(`/api/idp/logs/bulk/${encodeURIComponent(id)}`),

    // ---- アクセスポリシー(R7d)-------------------------------------------
    /** 帯に出す状態。Keycloak のフローと SPI の有無もここで分かる。 */
    policyStatus: () => request<PolicyStatus>("/api/idp/policy/status"),
    /** 条件式に書ける原子と上限(画面の早見表)。 */
    policyAtoms: () => request<PolicyAtoms>("/api/idp/policy/atoms"),
    /** 条件式の検証。**400 にならず** `{ok, error}` が返る(入力中に何度も呼ぶ)。 */
    checkPolicyExpression: (expression: string) =>
      request<{ ok: boolean; error: string }>("/api/idp/policy/check", {
        method: "POST",
        body: JSON.stringify({ expression }),
      }),
    /** 試算(記録しない)。 */
    previewPolicy: (body: {
      userId: string;
      username?: string;
      clientId?: string;
      ip?: string;
      cert?: boolean;
    }) =>
      request<PolicyDecision>("/api/idp/policy/preview", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    policySettings: () =>
      request<{ data: { enforce: boolean; applied: string } }>("/api/idp/policy/settings"),
    /**
     * `enforce` の切り替え。戻りの `keycloak` に反映の結果が入る
     * (`undone` があれば「反映できなかったので戻した」)。
     */
    savePolicySettings: (data: { enforce: boolean }) =>
      request<{
        data: { enforce: boolean; applied: string };
        changed: string[];
        keycloak: Record<string, unknown>;
      }>("/api/idp/policy/settings", { method: "PUT", body: JSON.stringify({ data }) }),
    /** Keycloak のブラウザフローを今すぐ確かめ直す(jar を入れた直後など)。 */
    recheckPolicyFlow: () =>
      request<Record<string, unknown>>("/api/idp/policy/flow/recheck", { method: "POST" }),
    ipGroups: () => request<{ items: IpGroup[] }>("/api/idp/policy/ip-groups"),
    ipGroup: (id: string) =>
      request<IpGroup>(`/api/idp/policy/ip-groups/${encodeURIComponent(id)}`),
    /** 作る。`entries` は 1 行 1 件の**貼り付けたテキスト**でもよい。 */
    createIpGroup: (data: { displayName: string; note?: string; entries: string }) =>
      request<IpGroup>("/api/idp/policy/ip-groups", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    updateIpGroup: (
      id: string,
      data: { displayName?: string; note?: string; entries?: string },
    ) =>
      request<IpGroup>(`/api/idp/policy/ip-groups/${encodeURIComponent(id)}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    /** 消す。**どこかの条件式で使われていたら 409**。 */
    deleteIpGroup: (id: string) =>
      request<{ id: string; displayName: string }>(
        `/api/idp/policy/ip-groups/${encodeURIComponent(id)}`,
        { method: "DELETE" },
      ),
    policyTemplates: () =>
      request<{ items: PolicyTemplate[] }>("/api/idp/policy/templates"),
    createPolicyTemplate: (data: {
      key: string;
      displayName?: string;
      expression: string;
    }) =>
      request<PolicyTemplate>("/api/idp/policy/templates", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    updatePolicyTemplate: (
      id: string,
      data: { key?: string; displayName?: string; expression?: string },
    ) =>
      request<PolicyTemplate>(`/api/idp/policy/templates/${encodeURIComponent(id)}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    deletePolicyTemplate: (id: string) =>
      request<{ id: string; key: string }>(
        `/api/idp/policy/templates/${encodeURIComponent(id)}`,
        { method: "DELETE" },
      ),
    policyGroups: () => request<{ items: PolicyGroup[] }>("/api/idp/policy/groups"),
    policyGroup: (id: string) =>
      request<PolicyGroupDetail>(`/api/idp/policy/groups/${encodeURIComponent(id)}`),
    /** SP トグルに出すサービスの一覧(新規作成の画面用)。 */
    policySps: () =>
      request<{ items: PolicyGroupSp[] }>("/api/idp/policy/groups/service-providers"),
    createPolicyGroup: (data: PolicyGroupForm) =>
      request<PolicyGroupDetail>("/api/idp/policy/groups", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    updatePolicyGroup: (id: string, data: PolicyGroupForm) =>
      request<PolicyGroupDetail>(`/api/idp/policy/groups/${encodeURIComponent(id)}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    /** 消す。**割当が残っていたら 409**。 */
    deletePolicyGroup: (id: string) =>
      request<{ id: string; displayName: string }>(
        `/api/idp/policy/groups/${encodeURIComponent(id)}`,
        { method: "DELETE" },
      ),
    /** ユーザーにポリシーを割り当てる(認可は他の変更と同じ)。 */
    setUserPolicyGroup: (id: string, groupId: string) =>
      request<{ userId: string; username: string; groupName: string }>(
        `/api/idp/users/${encodeURIComponent(id)}/policy-group`,
        { method: "PUT", body: JSON.stringify({ groupId }) },
      ),
    /** 割当を外す(既定のポリシーに戻る)。 */
    clearUserPolicyGroup: (id: string) =>
      request<{ userId: string; username: string; groupName: string }>(
        `/api/idp/users/${encodeURIComponent(id)}/policy-group`,
        { method: "DELETE" },
      ),
    /** 判定ログ(拒否と追加認証だけ記録される)。 */
    idpLogPolicy: (params: URLSearchParams) =>
      request<Page<PolicyDecisionRow>>(`/api/idp/logs/policy?${params.toString()}`),

    // ---- カスタム属性 ----------------------------------------------------
    idpCustomAttrs: () => request<CustomAttrs>("/api/idp/custom-attributes"),
    createIdpCustomAttr: (body: { name: string; displayName: string }) =>
      request<CustomAttr>("/api/idp/custom-attributes", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    deleteIdpCustomAttr: (name: string, force = false) =>
      request<{ name: string; forced: boolean; inUse: number }>(
        `/api/idp/custom-attributes/${encodeURIComponent(name)}${force ? "?force=true" : ""}`,
        { method: "DELETE" },
      ),

    // ---- ドメイン設定 ----------------------------------------------------
    idpDomain: () => request<DomainSettings>("/api/idp/domain"),
    saveIdpDomain: (form: DomainForm) =>
      request<DomainUpdated>("/api/idp/domain", {
        method: "PUT",
        body: JSON.stringify(form),
      }),
    /** ポータルに出す説明文(ログインしていれば誰でも読める)。 */
    idpPasswordNote: () => request<{ note: string }>("/api/idp/domain/password-note"),

    // ---- 端末証明書(R7e)-------------------------------------------------
    idpDeviceCerts: (params: URLSearchParams) =>
      request<DeviceCertList>(`/api/idp/device-certs?${params.toString()}`),
    /**
     * 発行する。**戻りの `p12_password` はこの1回だけ**(あとからは読めない)。
     * パスワードはメールに入れない(発行画面でだけ見せる)。
     */
    issueIdpDeviceCert: (body: {
      user_id: string;
      os: DeviceCertOs;
      note: string;
      mail_subject_note: string;
      send_mail: boolean;
      lang: DeviceCertLang;
    }) =>
      request<DeviceCertIssued>("/api/idp/device-certs", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    revokeIdpDeviceCert: (id: string, reason: string) =>
      request<DeviceCertItem>(`/api/idp/device-certs/${encodeURIComponent(id)}/revoke`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),
    /** メールを送り直す。**古いダウンロードリンクは無効になる**。 */
    mailIdpDeviceCert: (id: string, lang: DeviceCertLang) =>
      request<DeviceCertMailed>(`/api/idp/device-certs/${encodeURIComponent(id)}/mail`, {
        method: "POST",
        body: JSON.stringify({ lang }),
      }),
    /** ユーザー詳細の「入替リクエスト」。KPI の `revoke_requests` がこれを数える。 */
    requestReplaceIdpDeviceCert: (id: string, note: string) =>
      request<DeviceCertItem>(
        `/api/idp/device-certs/${encodeURIComponent(id)}/replace-request`,
        { method: "POST", body: JSON.stringify({ note }) },
      ),
    updateIdpDeviceCert: (
      id: string,
      body: { note?: string; mail_subject_note?: string },
    ) =>
      request<DeviceCertItem>(`/api/idp/device-certs/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    idpDeviceCertEvents: (id: string) =>
      request<{ items: DeviceCertEvent[] }>(
        `/api/idp/device-certs/${encodeURIComponent(id)}/events`,
      ),
    /** メール文面のプレビュー(差し込みを埋めた結果をサーバーが返す)。 */
    idpDeviceCertMailPreview: (params: URLSearchParams) =>
      request<DeviceCertMailText>(`/api/idp/device-certs/mail-preview?${params.toString()}`),
    /** ユーザー詳細の小表。 */
    idpUserDeviceCerts: (userId: string) =>
      request<{ items: DeviceCert[] }>(
        `/api/idp/users/${encodeURIComponent(userId)}/device-certs`,
      ),

    /**
     * ダウンロードの確認(ログイン不要)。**消費しない**ので、確認画面を出す前に
     * 1 回だけ呼ぶ。トークンは URL の fragment から読んだものを **body** に載せる
     * — パスに入れると Caddy / uvicorn のアクセスログに残る(fix1 高 1)。
     */
    deviceCertCheck: (token: string) =>
      request<{ ok: boolean }>("/api/idp/dc/check", {
        method: "POST",
        body: JSON.stringify({ token }),
      }),
    /**
     * 一式(p12)を落とす。**1 回限り**で、成功すると同じトークンは使えなくなる。
     * blob で受けるので `request` は通さない(JSON ではない)。
     */
    deviceCertDownload: async (token: string) => {
      const res = await fetch(`${apiBase}/api/idp/dc/download`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) {
      if (res.status === 401) window.dispatchEvent(new Event("itmgmt-unauthorized"));
        const body = (await res.json().catch(() => null)) as
          | { error?: { code?: string; message?: string; reason?: string } }
          | null;
        throw new ApiError(
          res.status,
          body?.error?.code ?? "error",
          body?.error?.message ?? `ファイルを取得できなかった (${res.status})`,
        );
      }
      const dispo = res.headers.get("Content-Disposition") ?? "";
      const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(dispo);
      return {
        blob: await res.blob(),
        filename: m ? decodeURIComponent(m[1]) : "device-cert.p12",
      };
    },

    // ---- API クライアント -------------------------------------------------
    idpApiClients: (params: URLSearchParams) =>
      request<Page<ApiClientRow>>(`/api/idp/api-clients?${params.toString()}`),
    idpApiClient: (id: number) => request<ApiClientDetail>(`/api/idp/api-clients/${id}`),
    /** 作る。**戻りの `secret` はこの1回だけ**(あとからは見られない)。 */
    createIdpApiClient: (body: { client_id: string; note: string; scopes: string[] }) =>
      request<ApiClientCreated>("/api/idp/api-clients", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    updateIdpApiClient: (id: number, body: { note?: string; scopes?: string[] }) =>
      request<ApiClientRow>(`/api/idp/api-clients/${id}`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    deleteIdpApiClient: (id: number) =>
      request<{ client_id: string; warning: string }>(`/api/idp/api-clients/${id}`, {
        method: "DELETE",
      }),
    rotateIdpApiClientSecret: (id: number) =>
      request<{ clientId: string; secret: string }>(`/api/idp/api-clients/${id}/secret/rotate`, {
        method: "POST",
      }),

    // ---- 電話帳同期(CS1)-------------------------------------------------
    contactsSetting: () => request<ContactsSetting>("/api/idp/contacts/settings"),
    saveContactsSetting: (data: Partial<ContactsSetting>) =>
      request<ContactsSetting>("/api/idp/contacts/settings", {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    testContacts: () =>
      request<ConnectorTestResult>("/api/idp/contacts/test", { method: "POST" }),
    contactBooks: () => request<ContactBook[]>("/api/idp/contacts/books"),
    saveContactBook: (id: number, data: ContactBookInput) =>
      request<ContactBook>(`/api/idp/contacts/books/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    /** 個別指定。`mode: null` で指定を外す。 */
    setContactMembers: (
      id: number,
      data: { employee_ids: number[]; roles: ContactRole[]; mode: ContactMode | null },
    ) =>
      request<{ changed: number }>(`/api/idp/contacts/books/${id}/members`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    contactGroups: () => request<DynGroupBrief[]>("/api/idp/contacts/groups"),
    contactPreview: (id: number) =>
      request<ContactPreview>(`/api/idp/contacts/books/${id}/preview`),
    syncContacts: (id: number) =>
      request<{ run_id: number }>(`/api/idp/contacts/books/${id}/sync`, { method: "POST" }),
    contactRuns: (id: number) =>
      request<ContactSyncRun[]>(`/api/idp/contacts/books/${id}/runs`),
    contactWithdrawals: () => request<ContactWithdrawal[]>("/api/idp/contacts/withdrawals"),
    cancelContactWithdrawal: (employeeId: number) =>
      request<ContactWithdrawal[]>(`/api/idp/contacts/withdrawals/${employeeId}/cancel`, {
        method: "POST",
      }),

    // ---- DLP(R6c)-----------------------------------------------------------
    dlpOverview: () => request<DlpOverview>("/api/dlp/overview"),
    dlpDevices: (params: URLSearchParams) =>
      request<Page<DlpDevice>>(`/api/dlp/devices?${params.toString()}`),
    dlpDevice: (id: number) => request<DlpDeviceDetail>(`/api/dlp/devices/${id}`),
    /** USB モードの変更(dlp:admin)。null で「既定に従う」。 */
    dlpPatchDevice: (id: number, body: { usb_mode: DlpUsbMode | null }) =>
      request<DlpDeviceDetail>(`/api/dlp/devices/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    /**
     * 端末の鍵を消す(dlp:admin、監査 dlp.device.reset_key)。エージェントを入れ直した端末用。
     * 次の poll でサーバーが新しい鍵を発行する(初回信頼。docs/dlp.md §5)。
     */
    dlpResetDeviceKey: (id: number) =>
      request<unknown>(`/api/dlp/devices/${id}/reset-key`, { method: "POST" }),
    dlpScans: (params: URLSearchParams) =>
      request<Page<DlpScan>>(`/api/dlp/scans?${params.toString()}`),
    dlpScan: (id: number) => request<DlpScan>(`/api/dlp/scans/${id}`),
    dlpScanFindings: (id: number, params: URLSearchParams) =>
      request<Page<DlpFinding>>(`/api/dlp/scans/${id}/findings?${params.toString()}`),
    /** 手動スキャン。同じ端末に進行中があれば 409。 */
    dlpRequestScan: (body: { device_id: number; policy_id?: number | null; note?: string }) =>
      request<DlpScan>("/api/dlp/scans", { method: "POST", body: JSON.stringify(body) }),
    dlpCancelScan: (id: number) =>
      request<DlpScan>(`/api/dlp/scans/${id}/cancel`, { method: "POST" }),
    dlpReviewScan: (id: number, body: { note: string }) =>
      request<DlpScan>(`/api/dlp/scans/${id}/review`, { method: "POST", body: JSON.stringify(body) }),
    dlpPolicies: () => request<Page<DlpPolicy>>("/api/dlp/policies"),
    /** id が null なら新規(POST)、あれば更新(PUT)。 */
    dlpSavePolicy: (id: number | null, body: DlpPolicyInput) =>
      request<DlpPolicy>(id == null ? "/api/dlp/policies" : `/api/dlp/policies/${id}`, {
        method: id == null ? "POST" : "PUT",
        body: JSON.stringify(body),
      }),
    dlpDeletePolicy: (id: number) =>
      request<void>(`/api/dlp/policies/${id}`, { method: "DELETE" }),
    dlpUsbEvents: (params: URLSearchParams) =>
      request<Page<DlpUsbEvent>>(`/api/dlp/usb/events?${params.toString()}`),
    dlpUploadEvents: (params: URLSearchParams) =>
      request<Page<DlpUploadEvent>>(`/api/dlp/upload/events?${params.toString()}`),
    dlpUploadDomains: (params: URLSearchParams) =>
      request<Page<DlpUploadDomain>>(`/api/dlp/upload/domains?${params.toString()}`),
    /** 追加(id = null)/ 更新(dlp:admin)。builtin の domain 変更は 400、同名は 409 */
    dlpSaveUploadDomain: (id: number | null, body: DlpUploadDomainInput) =>
      request<DlpUploadDomain>(id == null ? "/api/dlp/upload/domains" : `/api/dlp/upload/domains/${id}`, {
        method: id == null ? "POST" : "PUT",
        body: JSON.stringify(body),
      }),
    /** builtin は 409 */
    dlpDeleteUploadDomain: (id: number) =>
      request<void>(`/api/dlp/upload/domains/${id}`, { method: "DELETE" }),
    mailMessages: (kind: "my" | "approvals" | "messages", params: URLSearchParams, signal?: AbortSignal) =>
      request<Page<MailMessage>>(`/api/dlp/mail/${kind}?${params}`, { signal }),
    mailDetail: (uid: string, signal?: AbortSignal) => request<MailDetail>(`/api/dlp/mail/messages/${uid}`, { signal }),
    mailChange: (uid: string, operation: string, body: unknown) => request<MailDetail>(`/api/dlp/mail/messages/${uid}/${operation}`, { method: "POST", body: JSON.stringify(body) }),
    /** 添付ダウンロードリンク。失効は 409(message: not_sent / state_changed)、viewer への個別記録は 404。 */
    mailRevoke: (uid: string) => request<MailDetail>(`/api/dlp/mail/messages/${uid}/files/revoke`, { method: "POST", body: "{}" }),
    mailRevokeMany: (uids: string[]) => request<MailRevokeManyResult[]>("/api/dlp/mail/messages/files/revoke-many", { method: "POST", body: JSON.stringify({ uids }) }),
    mailFileDownloads: (uid: string, fileId: number, signal?: AbortSignal) => request<MailDownloadRecord[]>(`/api/dlp/mail/messages/${uid}/files/${fileId}/downloads`, { signal }),
    mailFileDownloadsPage: (params: URLSearchParams, signal?: AbortSignal) => request<Page<MailDownloadRecord | MailDownloadSummary>>(`/api/dlp/mail/file-downloads?${params}`, { signal }),
    mailRules: async () => {
      const first = await request<Page<MailRule>>("/api/dlp/mail/rules?limit=200");
      const items = [...first.items];
      while (items.length < first.total) {
        const next = await request<Page<MailRule>>(`/api/dlp/mail/rules?limit=200&offset=${items.length}`);
        if (!next.items.length) break;
        items.push(...next.items);
      }
      return { items, total: first.total };
    },
    mailSaveRule: (id: number | null, body: MailRuleInput) => request<MailRule>(`/api/dlp/mail/rules${id === null ? "" : `/${id}`}`, { method: id === null ? "POST" : "PUT", body: JSON.stringify(body) }),
    mailDeleteRule: (id: number) => request<void>(`/api/dlp/mail/rules/${id}`, { method: "DELETE" }),
    mailStatus: () => request<MailStatus>("/api/dlp/mail/status"),
    mailInboundSearch: (body: MailInboundSearch, signal?: AbortSignal) => request<MailInboundPage>("/api/dlp/mail/inbound/search", { method: "POST", body: JSON.stringify(body), signal }),
    mailInboundStats: (days: number, signal?: AbortSignal) => request<MailInboundStats>(`/api/dlp/mail/inbound/stats?days=${days}`, { signal }),
    mailInboundDetail: (uid: string, signal?: AbortSignal) => request<MailInboundDetail>(`/api/dlp/mail/inbound/${encodeURIComponent(uid)}`, { signal }),
    dlpSettings: () => request<DlpSettings>("/api/dlp/settings"),
    dlpSaveSettings: (body: DlpSettings) =>
      request<DlpSettings>("/api/dlp/settings", { method: "PUT", body: JSON.stringify(body) }),

    /** CSV / Excel を取り出す。Authorization が要るので fetch して blob にする。 */
    download: async (path: string) => {
      const token = getToken();
      const res = await fetch(apiBase + path, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        cache: "no-store",
      });
      if (!res.ok) {
      if (res.status === 401) window.dispatchEvent(new Event("itmgmt-unauthorized"));
        const body = (await res.json().catch(() => null)) as
          | { error?: { code?: string; message?: string; reason?: string } }
          | null;
        throw new ApiError(
          res.status,
          body?.error?.code ?? "error",
          body?.error?.message ?? `ファイルを取得できなかった (${res.status})`,
        );
      }
      const dispo = res.headers.get("Content-Disposition") ?? "";
      const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(dispo);
      return {
        blob: await res.blob(),
        filename: m ? decodeURIComponent(m[1]) : path.split("/").pop() || "download",
      };
    },

    /** 図(SVG / Mermaid)を取り出す。別タブで開くにも Authorization が要るので fetch で取る。 */
    chartFile: async (path: string) => {
      const token = getToken();
      const res = await fetch(apiBase + path, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        cache: "no-store",
      });
      if (!res.ok) throw new ApiError(res.status, "error", `図を取得できなかった (${res.status})`);
      return await res.blob();
    },
  };
}

export type Api = ReturnType<typeof createApi>;
